

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { usePermission } from '../../auth/usePermission';
import { useModuleEntitlements } from '../../hooks/useModuleEntitlements';
import { getApiErrorMessage, isModuleNotEntitledError } from '../../utils/apiErrors';
import {
  Card,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Autocomplete,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Button,
  DialogTitle,
  Modal,
} from "@mui/material";
import {
  Delete,
  Edit,
  Save,
  Close,
  FileCopy,
  Visibility,
  // Download 
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import ThemedCustomStepper from 'components/CustomStepper';
import { Download, LocalShipping, Payment, Description, Info, Receipt, PictureAsPdf } from '@mui/icons-material';
import OrderHeader from 'components/OrderHeader';
import CustomTable from 'components/CustomTables';
import EditOrderModal from 'components/EditOrderModal/EditOrderModal';
import AttachmentUploadModal from 'components/CustomModals/AttachmentUploadModal';
import Attachments from 'layouts/dashboard/components/Attachments';
// import ThemedCustomStepper from 'components/CustomStepper';
import CloneOrderModal from 'components/CloneOrderModal/CloneOrderModal';
import { Link } from 'react-router-dom';
import { formatDate } from 'utils/DateTimeConversion';
import { useTheme } from '@mui/material/styles';
import ActivityModal from 'components/CustomModals/CustomActivityModal';
import Activities from 'layouts/dashboard/components/Activities';
import Icon from "@mui/material/Icon";
import { useSnackbar } from 'notistack';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Loader2 } from 'lucide-react';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { downloadPrivateObject } from 'utils/S3';
import { serializeEmailAttachments } from 'utils/emailAttachments';
import CustomFieldsSection from "components/CustomFields/CustomFieldsSection";
import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import StatusStepperConfirmDialog from 'components/StatusStepperConfirmDialog';
// First, import the currencies list if it's not already imported
import { currencies } from 'utils/CurrencyList';
import { Skeleton } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import EmailModal from 'components/EmailModal';
import InvoiceViewer from './InvoiceViewer';
import colors from "assets/theme/base/colors";
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

import AttachmentIcon from '@mui/icons-material/Attachment';
import EventIcon from '@mui/icons-material/Event';
import UnauthorizedAccess from 'components/StyledAccessDenied';
import { frontendLogger } from '../../utils/logger';
// import DialogTitle from 'assets/theme/components/dialog/dialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import { Avatar } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MDUserAvatar from 'components/MDUserAvatar';
import EchoComposer from 'components/EchoComposer/EchoComposer';
import RecordPageV2, { TabStack } from "components/v2/record/RecordPageV2";
import { HeroBadge } from "components/v2/record/RecordHero";
import { StatusBadge } from "layouts/crm/account360/RelatedList";
import ActivityTimeline from "layouts/crm/account360/ActivityTimeline";
import { relativeTime } from "layouts/crm/account360/format";
import { Building2, CalendarDays, CircleDollarSign, Clock, Hash, History, LayoutGrid, ListOrdered, Package, Paperclip, Receipt as ReceiptIcon, Truck, UserRound, Wallet } from "lucide-react";
import UserLink from "components/v2/UserLink";

// Add this utility function near the top of the component, after the imports
const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function OrderDetail() {
  const { user, axiosInstance } = useAuth();
  const { isSupportUser } = usePermission('orders');
  // UI v2 record layout (design-reference "Sales · Deal record").
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const hasTasksModule = isModuleEnabled('TASKS');
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [salesOrder, setSalesOrder] = useState(null);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const orderSteps = ['New', 'Processing', 'Shipped', 'Delivered', 'Closed'];
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [orderItems, setOrderItems] = useState([]); // Your order items state
  const [activities, setActivities] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [viewLoadingId, setViewLoadingId] = useState(null);
  const [currentInvoiceHtml, setCurrentInvoiceHtml] = useState('');
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);
  const [editedInvoice, setEditedInvoice] = useState(null);
  const [editedInvoiceItems, setEditedInvoiceItems] = useState([]);
  const [openEditInvoiceModal, setOpenEditInvoiceModal] = useState(false);
  // Define state for cloned fields
  const [clonedFields, setClonedFields] = useState({}); // Add this line
  const [users, setUsers] = useState([]);
  const customFieldsRef = useRef(null); // Added to state declarations
  const { enqueueSnackbar } = useSnackbar();
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const [newActivity, setNewActivity] = useState({
    order_id: id,
    type: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [customFields, setCustomFields] = useState([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    content: ''
  });
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [emailContext, setEmailContext] = useState({});
  const [emailAttachments, setEmailAttachments] = useState([]);
  // Add this with other state declarations at the top
  const [isEmailButtonLoading, setIsEmailButtonLoading] = useState(false);
  const [openViewInvoiceModal, setOpenViewInvoiceModal] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewInvoiceItems, setViewInvoiceItems] = useState([]);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  // Add this with other state declarations at the top of the OrderDetail component
  const [isInvoiceButtonLoading, setIsInvoiceButtonLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isEditInvoiceLoading, setIsEditInvoiceLoading] = useState(false);
  const [isOrderPdfDownloading, setIsOrderPdfDownloading] = useState(false);
  const [orderPdfDownloadId, setOrderPdfDownloadId] = useState(null);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [invoicePercent, setInvoicePercent] = useState(100);
  const [invoicePercentError, setInvoicePercentError] = useState('');
  // const [isInvoiceButtonLoading, setIsInvoiceButtonLoading] = useState(false);

  // Add state for split mode and invoice amount
  const [splitMode, setSplitMode] = useState('percentage'); // 'percentage' or 'amount'
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const entityType = 'orders'; // Add this constant
  // Add this with other utility functions near the top of the file
  const formatInvoiceDate = (dateString) => {
    if (!dateString) return '-';
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

   const [sectionOrder, setSectionOrder] = useState([]);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);

  const fetchLeadName = async (leadId) => {
    if (nameCache[`lead_${leadId}`]) {
      return nameCache[`lead_${leadId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/leads/${leadId}`);
      const leadName = response.data.full_lead_name || `Lead #${leadId}`;
      setNameCache(prev => ({ ...prev, [`lead_${leadId}`]: leadName }));
      return leadName;
    } catch (error) {
      console.error('Error fetching lead name:', error);
      return `Lead #${leadId}`;
    }
  };
  // Function to fetch account name
  const fetchAccountName = async (accountId) => {
    if (nameCache[`account_${accountId}`]) {
      return nameCache[`account_${accountId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/accounts/${accountId}`);
      const accountName = response.data.name || `Account #${accountId}`;
      setNameCache(prev => ({ ...prev, [`account_${accountId}`]: accountName }));
      return accountName;
    } catch (error) {
      console.error('Error fetching account name:', error);
      return `Account #${accountId}`;
    }
  };

  // Function to fetch deal name
  const fetchDealName = async (dealId) => {
    if (nameCache[`deal_${dealId}`]) {
      return nameCache[`deal_${dealId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/deals/${dealId}`);
      const dealName = response.data.name || `Deal #${dealId}`;
      setNameCache(prev => ({ ...prev, [`deal_${dealId}`]: dealName }));
      return dealName;
    } catch (error) {
      console.error('Error fetching deal name:', error);
      return `Deal #${dealId}`;
    }
  };

  // Function to fetch contact name
  const fetchContactName = async (contactId) => {
    if (nameCache[`contact_${contactId}`]) {
      return nameCache[`contact_${contactId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/contacts/${contactId}`);
      const contactName = `${response.data.first_name || ''} ${response.data.last_name || ''}`.trim() || `Contact #${contactId}`;
      setNameCache(prev => ({ ...prev, [`contact_${contactId}`]: contactName }));
      return contactName;
    } catch (error) {
      console.error('Error fetching contact name:', error);
      return `Contact #${contactId}`;
    }
  };

  // Function to fetch entity name based on type
  const fetchEntityName = async (entityType, entityId) => {
    switch (entityType.toLowerCase()) {
      case 'lead':
        return await fetchLeadName(entityId);
      case 'account':
        return await fetchAccountName(entityId);
      case 'deal':
        return await fetchDealName(entityId);
      case 'contact':
        return await fetchContactName(entityId);
      case 'order':
        return await fetchOrderName(entityId);
      case 'invoice':
        return await fetchInvoiceName(entityId);
      case 'quote':
        return await fetchQuoteName(entityId);
      case 'product':
        return await fetchProductName(entityId);
      case 'case':
        return await fetchCaseName(entityId);
      default:
        return `${entityType} #${entityId}`;
    }
  };

  // Function to fetch user name
  const fetchUserName = async (userId) => {
    if (nameCache[`user_${userId}`]) {
      return nameCache[`user_${userId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/users/${userId}`);
      const userName = response.data.fullname || response.data.full_name || response.data.username;
      setNameCache(prev => ({ ...prev, [`user_${userId}`]: userName }));
      return userName;
    } catch (error) {
      console.error('Error fetching user name:', error);
      return `User ${userId}`;
    }
  };

  // Function to fetch order name
  const fetchOrderName = async (orderId) => {
    if (nameCache[`order_${orderId}`]) {
      return nameCache[`order_${orderId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/orders/${orderId}`);
      const orderName = response.data.order_number || `Order #${orderId}`;
      setNameCache(prev => ({ ...prev, [`order_${orderId}`]: orderName }));
      return orderName;
    } catch (error) {
      console.error('Error fetching order name:', error);
      return `Order #${orderId}`;
    }
  };

  // Function to fetch invoice name
  const fetchInvoiceName = async (invoiceId) => {
    if (nameCache[`invoice_${invoiceId}`]) {
      return nameCache[`invoice_${invoiceId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/invoices/${invoiceId}`);
      const invoiceName = `Invoice #${response.data.invoice_number || invoiceId}`;
      setNameCache(prev => ({ ...prev, [`invoice_${invoiceId}`]: invoiceName }));
      return invoiceName;
    } catch (error) {
      console.error('Error fetching invoice name:', error);
      return `Invoice #${invoiceId}`;
    }
  };

  // Function to fetch quote name
  const fetchQuoteName = async (quoteId) => {
    if (nameCache[`quote_${quoteId}`]) {
      return nameCache[`quote_${quoteId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/quotations/${quoteId}`);
      const quoteName = response.data.quote_name || `Quote #${quoteId}`;
      setNameCache(prev => ({ ...prev, [`quote_${quoteId}`]: quoteName }));
      return quoteName;
    } catch (error) {
      console.error('Error fetching quote name:', error);
      return `Quote #${quoteId}`;
    }
  };

  // Function to fetch product name
  const fetchProductName = async (productId) => {
    if (nameCache[`product_${productId}`]) {
      return nameCache[`product_${productId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/products/${productId}`);
      const productName = response.data.name || `Product #${productId}`;
      setNameCache(prev => ({ ...prev, [`product_${productId}`]: productName }));
      return productName;
    } catch (error) {
      console.error('Error fetching product name:', error);
      return `Product #${productId}`;
    }
  };

  // Function to fetch case name
  const fetchCaseName = async (caseId) => {
    if (nameCache[`case_${caseId}`]) {
      return nameCache[`case_${caseId}`];
    }
    
    try {
      const response = await axiosInstance.get(`/cases/${caseId}`);
      const caseName = response.data.case_number || `Case #${caseId}`;
      setNameCache(prev => ({ ...prev, [`case_${caseId}`]: caseName }));
      return caseName;
    } catch (error) {
      console.error('Error fetching case name:', error);
      return `Case #${caseId}`;
    }
  };

   useEffect(() => {
      const fetchGlobalPreferences = async () => {
        try {
          const res = await axiosInstance.get('/preferences/global');
          const prefs = res.data?.section_preferences?.order;
          if (prefs) {
            if (prefs.order) setSectionOrder(prefs.order);
            if (prefs.hidden) setHiddenSections(prefs.hidden);
          } else {
            // fallback to default order
            setSectionOrder([
              'Order Information',
              'Shipping & Payment',
              'Transport Details',
              'Custom Field Information',
              'Order Items',
              'Audit Information',
            ]);
            setHiddenSections([]);
          }
        } catch (e) {
          // fallback to default order
          setSectionOrder([
            'Order Information',
            'Shipping & Payment',
            'Transport Details',
            'Custom Field Information',
            'Order Items',
            'Audit Information',
          ]);
          setHiddenSections([]);
        } finally {
          setLoadingSections(false);
        }
      };
      fetchGlobalPreferences();
      // eslint-disable-next-line
    }, []);
  
    // Compose visible sections in order
    const visibleSections = sectionOrder.filter(key => !hiddenSections.includes(key));
  

  // Add this with other style constants
  const actionIconStyle = {
    padding: '4px',
    transition: 'all 0.2s',
    '&:hover': {
      transform: 'scale(1.1)',
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  };
  useEffect(() => {
    fetchAttachments();
  }, [id]);

  useEffect(() => {
    if (entitlementsLoading || !id) return;
    if (hasTasksModule) {
      fetchActivities();
    } else {
      setActivities([]);
    }
  }, [id, entitlementsLoading, hasTasksModule]);
  useEffect(() => {
    console.log('Attachments:', attachments);
  }, [attachments]);

  useEffect(() => {
    const getOrderData = async () => {
      setIsLoading(true); // Set loading state at start
      try {
        await fetchOrderData();
      } catch (error) {
        console.error('Error fetching order data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading order data',
          severity: 'error'
        });
      } finally {
        setIsLoading(false); // Clear loading state regardless of outcome
      }
    };

    if (id) {
      getOrderData();
    }
  }, [id]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get('/users/');
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Echo/Feed functionality functions
  const fetchFeedItems = async () => {
    if (!id) return;
    setIsFeedLoading(true);
    try {
      console.log('Fetching feed items for:', { entityType, id });
      const response = await axiosInstance.get(`/feed-items/entity/${entityType}/${id}`);
      console.log('Fetched feed items:', response.data);
      console.log('Feed items count:', response.data?.length || 0);
      setFeedItems(response.data || []);
    } catch (error) {
      console.error('Error fetching feed items:', error);
      console.error('Error response:', error.response?.data);
      setFeedItems([]);
    } finally {
      setIsFeedLoading(false);
    }
  };

  const handleEchoSubmit = async (feedItem) => {
    // The feedItem is the response from the API after successful creation
    console.log('Feed item created:', feedItem);
    
    // Refresh the feed items to ensure consistency
    await fetchFeedItems();
    
    // The success message will be shown by the onSuccess callback
  };

  const handleEditFeedItem = async (editData) => {
    try {
      setIsEditingFeed(true);
      
      // Extract the feed item ID from the edit data
      const feedItemId = editData.feed_item_id;
      
      if (!feedItemId) {
        throw new Error('No feed item ID found in edit data');
      }
      
      console.log('=== EDIT FEED ITEM DEBUG ===');
      console.log('Feed Item ID:', feedItemId);
      console.log('Edit Data received:', editData);
      console.log('Object Type:', editData.object_type);
      console.log('Object ID:', editData.object_id);
      console.log('Body content:', editData.body);
      console.log('Mentions:', editData.mentions);
      
      // Make the PUT request to the correct endpoint with the feed item ID
      const response = await axiosInstance.put(`/feed-items/${feedItemId}`, {
        feeditem: {
          object_type: editData.object_type,
          object_id: editData.object_id,
          body: editData.body
          // Don't include mentions - backend will parse them from body automatically
        }
      });
      
      if (response.status === 200) {
        // Refresh the entire feed to get updated data and proper sorting
        await fetchFeedItems();
        
        setSnackbar({
          open: true,
          message: 'Message updated successfully!',
          severity: 'success',
        });
        
        // The EchoComposer will handle clearing the edit state via onSuccess callback
        // so we don't need to do it here
      }
    } catch (error) {
      console.error('Error updating feed item:', error);
      // setSnackbar({
      //   open: true,
      //   message: 'Error updating message',
      //   severity: 'error',
      // });
    } finally {
      setIsEditingFeed(false);
    }
  };

  const handleDeleteFeedItem = async (feedItemId) => {
    try {
      const response = await axiosInstance.delete(`/feed-items/${feedItemId}`);
      
      if (response.status === 200) {
        // Update the feed items state by removing the deleted item
        setFeedItems(prevItems => prevItems.filter(item => item.id !== feedItemId));
        
        setSnackbar({
          open: true,
          message: 'Message deleted successfully!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error deleting feed item:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting message',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = (feedItem) => {
    setItemToDelete(feedItem);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirmOk = async () => {
    if (itemToDelete) {
      await handleDeleteFeedItem(itemToDelete.id);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const startEditFeedItem = (feedItem) => {
    setEditingFeedItem(feedItem.id);
    setEditFeedContent(feedItem.feeditem.body);
  };

  const cancelEditFeedItem = () => {
    setEditingFeedItem(null);
    setEditFeedContent('');
  };

  const handleOpenEchoModal = () => {
    setIsEchoModalOpen(true);
  };

  const handleCloseEchoModal = () => {
    setIsEchoModalOpen(false);
  };
  const handleCreateEcho = async () => {
    // This will be handled by EchoComposer's onSubmit
    handleCloseEchoModal();
  };

  // Component for rendering mentions with immediate name display
  const MentionDisplay = ({ mention, fullMatch, entityTypeOrUser, entityId, mentions, navigate }) => {
    // Calculate display name immediately without async operations
    let displayName = '';
    
    if (entityId) {
      // For entity mentions, we'll need to fetch the name
      // But for now, let's show the entity type and ID
      displayName = `${entityTypeOrUser}:${entityId}`;
    } else {
      // For user mentions, use entity_name immediately if available
      if (mention && mention.entity_name) {
        displayName = mention.entity_name;
      } else {
        // Fallback to username
        displayName = entityTypeOrUser;
      }
    }

    return (
      <Box
        component="span"
        sx={{
          color: entityId ? 'info.main' : 'primary.main',
          fontWeight: 'bold',
          backgroundColor: entityId ? 'rgba(33, 150, 243, 0.08)' : 'rgba(103, 58, 183, 0.08)',
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          mx: 0.25,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: entityId ? 'rgba(33, 150, 243, 0.15)' : 'rgba(103, 58, 183, 0.15)',
          }
        }}
        onClick={() => {
          if (entityId) {
            // Navigate to the mentioned entity
            navigate(`/${entityTypeOrUser.toLowerCase()}s/${entityId}`);
          } else {
            // Handle user mention - navigate to user profile
            if (mention && mention.entity_id) {
              navigate(`/users/${mention.entity_id}/profile`);
            }
          }
        }}
      >
        {displayName}
      </Box>
    );
  };

  // Helper function to extract name parts from created_by_name
  const extractNameParts = (createdByUser, createdByName) => {
    let nameParts = {
      firstName: 'U',
      middleName: '',
      lastName: '',
      avatarUrl: null
    };

    if (createdByUser?.first_name && createdByUser?.last_name) {
      nameParts = {
        firstName: createdByUser.first_name,
        middleName: createdByUser.middle_name || '',
        lastName: createdByUser.last_name,
        avatarUrl: createdByUser.avatar || null
      };
    } else if (createdByName) {
      const namePartsArray = createdByName.split(' ').filter(part => part.trim());
      if (namePartsArray.length === 1) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: '',
          lastName: '',
          avatarUrl: null
        };
      } else if (namePartsArray.length >= 2) {
        nameParts = {
          firstName: namePartsArray[0],
          middleName: namePartsArray.slice(1, -1).join(' '),
          lastName: namePartsArray[namePartsArray.length - 1],
          avatarUrl: null
        };
      }
    }
    
    return nameParts;
  };

  // Enhanced feed content parser that uses async name fetching
  const parseFeedContent = (feedItem) => {
    if (!feedItem?.feeditem?.body) return '';
    
    const body = feedItem.feeditem.body;
    const mentions = feedItem.feeditem.mentions || [];
    
    console.log('Parsing feed content:', { body, mentions });
    
    // Parse mentions in the format @username (for users) or @EntityType:entityId (for entities)
    const parts = [];
    const mentionRegex = /@([A-Za-z0-9_.@]+)(?::([A-Za-z0-9_]+))?/g;
    let lastIndex = 0;
    let match;
    
    // Create a map of usernames to mention data for better matching
    const userMentionsMap = {};
    mentions.forEach(mention => {
      if (mention.type === 'user' && mention.entity_name) {
        // Try to match by username pattern in the body
        const usernamePattern = new RegExp(`@${mention.entity_name.replace(/\s+/g, '[._]')}`, 'i');
        if (usernamePattern.test(body)) {
          userMentionsMap[mention.entity_name] = mention;
        }
      }
    });
    
    while ((match = mentionRegex.exec(body)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {body.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      // Add mention as highlighted text with async name fetching
      const [fullMatch, firstPart, secondPart] = match;
      
      // Determine if this is a user mention or entity mention
      let entityTypeOrUser, entityId;
      
      if (secondPart) {
        // Format: @EntityType:entityId
        entityTypeOrUser = firstPart;
        entityId = secondPart;
      } else {
        // Format: @username (user mention)
        entityTypeOrUser = firstPart;
        entityId = null;
      }
      
      // Find the corresponding mention data
      let mentionData;
      if (entityId) {
        // For entity mentions: @EntityType:entityId
        mentionData = mentions.find(m => m.type === 'record' && m.entity_type === entityTypeOrUser && m.entity_id === entityId);
      } else {
        // For user mentions: @username - try multiple matching strategies
        // First, try exact match with entity_name
        mentionData = mentions.find(m => m.type === 'user' && m.entity_name === entityTypeOrUser);
        
        // If not found, try matching by username pattern
        if (!mentionData) {
          const userMention = mentions.find(m => {
            if (m.type === 'user' && m.entity_name) {
              // Check if the username in text matches the entity_name (with possible dots/underscores)
              const normalizedEntityName = m.entity_name.replace(/\s+/g, '[._]');
              const usernamePattern = new RegExp(`^${normalizedEntityName}$`, 'i');
              return usernamePattern.test(entityTypeOrUser);
            }
            return false;
          });
          if (userMention) {
            mentionData = userMention;
          }
        }
        
        // If still not found, try to find by position in the mentions array
        if (!mentionData) {
          const userMentions = mentions.filter(m => m.type === 'user');
          // Count how many user mentions we've seen so far in the text
          const textBeforeCursor = body.substring(0, match.index);
          const userMentionsBefore = (textBeforeCursor.match(/@[A-Za-z0-9_.@]+/g) || []).length;
          if (userMentionsBefore < userMentions.length) {
            mentionData = userMentions[userMentionsBefore];
          }
        }
      }
      
      parts.push(
        <MentionDisplay
          key={`mention-${match.index}`}
          mention={mentionData}
          fullMatch={fullMatch}
          entityTypeOrUser={entityTypeOrUser}
          entityId={entityId}
          mentions={mentions}
          navigate={navigate}
        />
      );
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // Add remaining text
    if (lastIndex < body.length) {
      parts.push(
        <span key={`text-end`}>
          {body.substring(lastIndex)}
        </span>
      );
    }
    
    return parts;
  };

  // Echo/Feed functionality state variables
  const [feedItems, setFeedItems] = useState([]);
  const [isFeedLoading, setIsFeedLoading] = useState(false);
  const [editingFeedItem, setEditingFeedItem] = useState(null);
  const [editFeedContent, setEditFeedContent] = useState('');
  const [isEditingFeed, setIsEditingFeed] = useState(false);
  const [isEchoModalOpen, setIsEchoModalOpen] = useState(false);
  const [newEchoContent, setNewEchoContent] = useState('');
  const [isCreatingEcho, setIsCreatingEcho] = useState(false);
  const [nameCache, setNameCache] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Fetch feed items on component mount
  useEffect(() => {
    if (id) {
      fetchFeedItems();
    }
  }, [id]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [suggestions, setSuggestions] = useState({
    names: [], // Product names
    // Add other suggestion lists as needed
  });

  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);

  const [productList, setProductList] = useState([]);

  const orderStages = ['Draft', 'In Progress', 'Completed', 'Cancelled'];
  const [activeStep, setActiveStep] = useState(0);
  const [orderStatusConfirm, setOrderStatusConfirm] = useState({ open: false, index: null });
  const [accountName, setAccountName] = useState(null);
  // Function to get active step based on order status
  const getActiveStepFromStatus = (status) => {
    switch (status) {
      case 'Draft':
        return 0;
      case 'In Progress':
        return 1;
      case 'Completed':
        return 2;
      case 'Cancelled':
        return 3;
      default:
        return 0;
    }
  };

  // Update active step when order data is fetched
  useEffect(() => {
    if (order) {
      const step = getActiveStepFromStatus(order.status);
      setActiveStep(step);
    }
  }, [order]);

  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get(`/custom-fields/orders`);
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);

  const auditFields = [
    { label: "Created By", field: "created_by_name", userIdField: "created_by", readOnly: true },
    { label: "Modified By", field: "updated_by_name", userIdField: "updated_by", readOnly: true },
    { label: "Created Date", field: "created_at", readOnly: true },
    { label: "Modified Date", field: "updated_at", readOnly: true },
  ];

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const [productsResponse] = await Promise.all([
          axiosInstance.get("/products/")
        ]);

        setSuggestions({
          names: productsResponse.data.map(product => product.name)
        });

        setProductList(productsResponse.data);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      }
    };

    fetchSuggestions();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axiosInstance.get('/accounts/');
      const formattedAccounts = response.data.map(account => ({
        value: account.id,
        label: account.name || `Account ${account.name}`,
        fullData: account,
        currency: account.currency
      }));
      setAccounts(formattedAccounts);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  useEffect(() => {
    if (openEditInvoiceModal) {
      fetchAccounts();
    }
  }, [openEditInvoiceModal]);

  const OrderDetailsSkeleton = () => (
    <MDBox p={3}>
      <Grid container spacing={3}>
        {/* Simulate Order Information section */}
        {[...Array(6)].map((_, index) => (
          <Grid item xs={12} md={6} key={`skeleton-field-${index}`}>
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={40} />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
        </Grid>

        {/* Simulate Related Information section */}
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} md={6} key={`skeleton-related-${index}`}>
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={40} />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
        </Grid>

        {/* Simulate Audit Information section */}
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} md={6} key={`skeleton-audit-${index}`}>
            <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={40} />
          </Grid>
        ))}
      </Grid>
    </MDBox>
  );


  const formatIndianNumber = (number) => {
    if (isNaN(number) || number === null || number === undefined) return "0.00";

    const [integerPart, decimalPart] = Number(number).toFixed(2).split(".");
    const lastThreeDigits = integerPart.slice(-3);
    const otherDigits = integerPart.slice(0, -3);

    let formattedNumber = "";
    if (otherDigits) {
      formattedNumber = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThreeDigits;
    } else {
      formattedNumber = lastThreeDigits;
    }

    return `${formattedNumber}.${decimalPart}`; // Always include two decimal places
  };

  // Prefer the order's own currency (e.g. AED). Account currency is only a
  // fallback — using account currency alone caused AED orders to render as ₹.
  const getOrderDisplayCurrency = (orderRecord = order) =>
    orderRecord?.currency || orderRecord?.account_currency || 'INR';

  // Format amount using the order (or explicit) currency code — never force INR
  // when a real code like AED/AUD/USD was passed.
  const formatCurrencyByAccount = (number, currencyCode) => {
    try {
      if (number === null || number === undefined) return "";

      const code =
        currencyCode ||
        currencies.find((c) => c.code === 'INR')?.code ||
        'INR';

      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: code,
        maximumFractionDigits: 2,
      }).format(number);
    } catch (error) {
      console.error("Error formatting currency:", error);
      return `${currencyCode || ''} ${number}`.trim();
    }
  };

  // Handle save changes in the modal
  const handleSaveOrderChanges = async (payload) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to edit orders.',
        severity: 'error',
      });
      return; // Exit the function early
    }

    // const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true; // Added validation call

    // if (Object.keys(formErrors).length > 0 || !isCustomFieldsValid) {
    //   setSnackbar({
    //     open: true,
    //     message: "Please fill the required fields before saving.",
    //     severity: 'error'
    //   });
    //   throw new Error("Validation failed: Required fields are missing"); // Throw error to stop execution
    // }
    // console.log('Form errors:', formErrors);
    // console.log('Custom fields validation result:', isCustomFieldsValid);
    try {
      console.log('Payload before saving:', payload);

      // Process removedOrderItems to ensure they are handled correctly
      const updatedPayload = {
        ...payload,
        removedOrderItems: payload.removedOrderItems || [],
        order: {
          ...payload.order,
          account_id: payload.order.account_id,
          deal_id: payload.order.deal_id || null,
          contact_id: payload.order.contact_id,
          transdocno: payload.order.transdocno || null,
          transid: payload.order.transid || null,
          transname: payload.order.transname || null,
          transmode: payload.order.transmode || null,
          vehno: payload.order.vehno || null,
          vehtype: payload.order.vehtype || null,

        }
      };

      console.log('Updated Payload:', updatedPayload);

      // Get current order data for comparison
      const currentOrderResponse = await axiosInstance.get(`/orders/${updatedPayload.order.id}`);
      const currentOrder = currentOrderResponse.data;
      const currentOrderItems = currentOrder.order_items || [];

      // Handle inventory updates for removed items (restore inventory)
      if (updatedPayload.removedOrderItems?.length > 0) {
        for (const itemId of updatedPayload.removedOrderItems) {
          const removedItem = currentOrderItems.find(item => item.id === itemId);
          if (removedItem) {
            try {
              // Check product type first
              const productResponse = await axiosInstance.get(`/products/${removedItem.product_id}`);
              const product = productResponse.data;

              // Only restore inventory for physical products
              if (product.product_type === 'Physical') {
                // Get current inventory
                const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${removedItem.product_id}`);
                const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === removedItem.product_id);

                if (inventoryItem) {
                  // Restore quantity to inventory
                  const newQuantity = inventoryItem.quantity + removedItem.quantity;
                  await axiosInstance.put(`/inventory/${inventoryItem.id}`, {
                    ...inventoryItem,
                    quantity: newQuantity,
                  });
                  setSnackbar({
                    open: true,
                    message: `Inventory restored for ${removedItem.product_name} (+${removedItem.quantity} units)`,
                    severity: 'info'
                  });
                }
              } else {
                console.log(`Skipping inventory restoration for non-physical product: ${removedItem.product_name}`);
              }
            } catch (error) {
              throw new Error(`Failed to restore inventory for ${removedItem.product_name}: ${error.message}`);
            }
          }
        }

        // Delete removed items
        const deletePromises = updatedPayload.removedOrderItems.map(itemId => {
          console.log('Deleting item with ID:', itemId);
          return axiosInstance.delete(`/orders/items/${itemId}`);
        });
        await Promise.all(deletePromises); // Wait for all deletions to complete

        console.log('Removed order items successfully');
      }

      // Step 1: Update order details
      const { id: orderId, ...orderPayloadWithoutId } = updatedPayload.order;
      console.log('Order Payload Without ID:', orderPayloadWithoutId);

      const updatedOrderResponse = await axiosInstance.put(
        `/orders/${orderId}`,
        orderPayloadWithoutId
      );

      // Step 2: Update account and contact names (mandatory)
      const updatedAccount = await axiosInstance.get(`/accounts/${updatedPayload.order.account_id}`);
      const updatedContact = await axiosInstance.get(`/contacts/${updatedPayload.order.contact_id}`);

      // Only fetch deal if deal_id is present and not null
      let updatedDeal = null;
      if (updatedPayload.order.deal_id) {
        updatedDeal = await axiosInstance.get(`/deals/${updatedPayload.order.deal_id}`);
      }

      // Update the order state with new names
      setOrder(prevOrder => ({
        ...prevOrder,
        account_name: updatedAccount.data.name,
        contact_name: updatedContact?.data?.first_name
          ? `${updatedContact.data.first_name} ${updatedContact.data.last_name || ''}`.trim()
          : updatedContact?.data?.last_name || '',
        deal_name: updatedDeal?.data?.name || '',
      }));

      // Process order items and handle inventory
      if (updatedPayload.orderItems?.length > 0) {
        for (const item of updatedPayload.orderItems) {
          try {
            const productId = item.product_id || await fetchOrCreateProduct(item);

            // First, check the product type
            const productResponse = await axiosInstance.get(`/products/${productId}`);
            const product = productResponse.data;

            // Ensure non-physical products have quantity=1
            if (product.product_type === 'Service' || product.product_type === 'Digital') {
              if (item.quantity !== 1) {
                item.quantity = 1;
                console.log(`Set quantity to 1 for non-physical product: ${item.product_name}`);
              }
            }

            const { id: itemId, ...itemPayloadWithoutId } = item;

            // Find existing order item if any
            const existingItem = currentOrderItems.find(oi => oi.id === itemId);

            // Only handle inventory for physical products
            if (product.product_type === 'Physical') {
              // Get current inventory state
              const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${productId}`);
              const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === productId);

              if (!inventoryItem) {
                throw new Error(`Inventory record not found for ${item.product_name}`);
              }

              let quantityDifference = 0;

              if (existingItem) {
                // Calculate quantity difference for existing items
                quantityDifference = item.quantity - existingItem.quantity;
              } else {
                // New item - full quantity needs to be deducted
                quantityDifference = item.quantity;
              }

              // Update inventory based on quantity difference
              if (quantityDifference !== 0) {
                const newQuantity = inventoryItem.quantity - quantityDifference;

                if (newQuantity < 0) {
                  throw new Error(
                    `Insufficient inventory for ${item.product_name}. ` +
                    `Available: ${inventoryItem.quantity}, ` +
                    `Required: ${Math.abs(quantityDifference)} additional units`
                  );
                }

                await axiosInstance.put(`/inventory/${inventoryItem.id}`, {
                  ...inventoryItem,
                  quantity: newQuantity,
                });

                setSnackbar({
                  open: true,
                  message: `Inventory ${quantityDifference > 0 ? 'reduced' : 'increased'} for ${item.product_name} (${quantityDifference > 0 ? '-' : '+'}${Math.abs(quantityDifference)} units)`,
                  severity: 'info'
                });
              }
            } else {
              // For non-physical products, just log that we're skipping inventory
              console.log(`Skipping inventory update for non-physical product: ${item.product_name}`);
            }

            // Update or create order item
            if (itemId) {
              await axiosInstance.put(`/orders/items/${itemId}`, {
                ...itemPayloadWithoutId,
                product_id: productId,
                order_id: orderId
              });
            } else {
              await axiosInstance.post('/orders/items/', {
                ...itemPayloadWithoutId,
                product_id: productId,
                order_id: orderId
              });
            }
          } catch (error) {
            throw new Error(`Error processing ${item.product_name}: ${error.message}`);
          }
        }
        console.log('Order items processed successfully');
      }

      // Handle inventory restoration if order is cancelled
      if (updatedPayload.order.status === 'Cancelled' && currentOrder.status !== 'Cancelled') {
        let restoredItems = [];
        for (const item of currentOrderItems) {
          try {
            // Check product type first
            const productResponse = await axiosInstance.get(`/products/${item.product_id}`);
            const product = productResponse.data;

            // Only restore inventory for physical products
            if (product.product_type === 'Physical') {
              const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${item.product_id}`);
              const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === item.product_id);

              if (inventoryItem) {
                const newQuantity = inventoryItem.quantity + item.quantity;
                await axiosInstance.put(`/inventory/${inventoryItem.id}`, {
                  ...inventoryItem,
                  quantity: newQuantity,
                });
                restoredItems.push(`${item.product_name} (+${item.quantity} units)`);
              }
            } else {
              console.log(`Skipping inventory restoration for non-physical product: ${item.product_name}`);
            }
          } catch (error) {
            throw new Error(`Failed to restore inventory for cancelled order: ${error.message}`);
          }
        }

        if (restoredItems.length > 0) {
          setSnackbar({
            open: true,
            message: `Order cancelled - Inventory restored: ${restoredItems.join(', ')}`,
            severity: 'info'
          });
        }
      }

      // Step 4: Refresh order data
      const refreshedOrderResponse = await axiosInstance.get(`/orders/${orderId}`);
      setOrder(refreshedOrderResponse.data);
      setOrderItems(refreshedOrderResponse.data.order_items);

      // Final success message
      setSnackbar({
        open: true,
        message: `Order ${updatedPayload.order.id} updated successfully with all inventory adjustments`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating order:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update order',
        severity: 'error'
      });
    }
  };


  // Helper functions
  const hasOrderFieldsChanged = (currentOrder, newOrder) => {
    const relevantFields = ['account_id', 'contact_id', 'deal_id', 'status', 'notes', 'total_amount', 'shipping_method', 'shipping_address', 'billing_address',];
    return relevantFields.some(field => {
      // Special handling for deal_id to treat undefined and null as equal
      if (field === 'deal_id') {
        return (currentOrder[field] || null) !== (newOrder[field] || null);
      }
      return currentOrder[field] !== newOrder[field];
    });
  };

  // Rest of the helper functions remain the same
  const hasItemsChanged = (currentItems, newItems = [], removedItems = []) => {
    if (removedItems.length > 0) return true;
    if (currentItems.length !== newItems.length) return true;

    return newItems.some(newItem => {
      const currentItem = currentItems.find(item => item.id === newItem.id);
      if (!currentItem) return true;

      // Add discount_percentage to the comparison
      return currentItem.quantity !== newItem.quantity ||
        currentItem.price !== newItem.price ||
        currentItem.discount_percentage !== newItem.discount_percentage;
    });
  };

  const handleRemovedItems = async (removedOrderItems, currentOrderItems) => {
    for (const itemId of removedOrderItems) {
      const removedItem = currentOrderItems.find(item => item.id === itemId);
      if (removedItem) {
        await restoreInventory(removedItem);
        await axiosInstance.delete(`/orders/items/${itemId}`);
      }
    }
  };

  const restoreInventory = async (item) => {
    // First get product details to check product_type
    const productResponse = await axiosInstance.get(`/products/${item.product_id}`);
    const product = productResponse.data;

    // Only restore inventory for physical products
    if (product.product_type === 'Physical') {
      const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${item.product_id}`);
      const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === item.product_id);

      if (inventoryItem) {
        const newQuantity = inventoryItem.quantity + item.quantity;
        await axiosInstance.put(`/inventory/${inventoryItem.id}`, {
          ...inventoryItem,
          quantity: newQuantity,
        });
        setSnackbar({
          open: true,
          message: `Inventory restored for ${item.product_name} (+${item.quantity} units)`,
          severity: 'info'
        });
      }
    } else if (product.product_type === 'Service' || product.product_type === 'Digital') {
      // For non-physical products, no inventory restoration needed
      // Make sure quantity remains as 1 for non-physical items
      if (item.quantity !== 1) {
        // Update this if you have access to update the item
        console.log(`Non-physical product ${item.product_name} quantity should be 1, found ${item.quantity}`);
      } else {
        console.log(`Skipping inventory restoration for ${product.product_type} product: ${item.product_name}`);
      }
    }
  };

  const handleOrderItems = async (orderItems, currentOrderItems, orderId) => {
    for (const item of orderItems) {
      try {
        const productId = item.product_id || await fetchOrCreateProduct(item);
        const { id: itemId, ...itemPayloadWithoutId } = item;

        // Get product details to check product_type first
        const productResponse = await axiosInstance.get(`/products/${productId}`);
        const product = productResponse.data;

        // Set a consistent quantity of 1 for non-physical products
        if (product.product_type === 'Service' || product.product_type === 'Digital') {
          item.quantity = 1;
        }

        const existingItem = currentOrderItems.find(oi => oi.id === itemId);
        if (!existingItem || hasItemDataChanged(existingItem, item)) {
          await updateInventoryAndItem(item, existingItem, productId, orderId, product);
        }
      } catch (error) {
        console.error(`Error processing item ${item.product_name}:`, error);
        throw new Error(`Error processing ${item.product_name}: ${error.message}`);
      }
    }
  };

  const hasItemDataChanged = (existingItem, newItem) => {
    return existingItem.quantity !== newItem.quantity ||
      existingItem.price !== newItem.price ||
      existingItem.discount_percentage !== newItem.discount_percentage;
  };

  const updateInventoryAndItem = async (item, existingItem, productId, orderId, productData = null) => {
    try {
      // Use provided product data or fetch it if not provided
      const product = productData || (await axiosInstance.get(`/products/${productId}`)).data;

      // Handle based on product type
      if (product.product_type === 'Physical') {
        // Only check inventory for physical products
        const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${productId}`);
        const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === productId);

        if (!inventoryItem) {
          throw new Error(`Inventory record not found for ${item.product_name}`);
        }

        const quantityDifference = existingItem
          ? item.quantity - existingItem.quantity
          : item.quantity;

        if (quantityDifference !== 0) {
          await updateInventoryQuantity(inventoryItem, quantityDifference, item.product_name);
        }
      } else if ((product.product_type === 'Service' || product.product_type === 'Digital')) {
        // For Service or Digital products, ensure quantity is 1 and show message
        if (item.quantity !== 1) {
          item.quantity = 1; // Fix quantity to be exactly 1

          setSnackbar({
            open: true,
            message: `Quantity set to 1 for ${product.product_type} product: ${item.product_name}`,
            severity: 'info'
          });
        }
      }

      // Always update or create the order item
      await updateOrCreateOrderItem(item, productId, orderId);
    } catch (error) {
      console.error(`Error updating inventory for ${item.product_name}:`, error);
      if (item && (item.product_type === 'Service' || item.product_type === 'Digital')) {
        // For non-physical products, show a warning but continue (don't fail the order)
        console.warn(`Continuing with non-physical product ${item.product_name} despite error:`, error);
        await updateOrCreateOrderItem(item, productId, orderId);
      } else {
        // For physical products or unknown product types, throw the error
        throw error;
      }
    }
  };

  const updateInventoryQuantity = async (inventoryItem, quantityDifference, productName) => {
    const newQuantity = inventoryItem.quantity - quantityDifference;

    if (newQuantity < 0) {
      throw new Error(
        `Insufficient inventory for ${productName}. ` +
        `Available: ${inventoryItem.quantity}, ` +
        `Required: ${Math.abs(quantityDifference)} additional units`
      );
    }

    await axiosInstance.put(`/inventory/${inventoryItem.id}`, {
      ...inventoryItem,
      quantity: newQuantity,
    });

    setSnackbar({
      open: true,
      message: `Inventory ${quantityDifference > 0 ? 'reduced' : 'increased'} for ${productName} (${quantityDifference > 0 ? '-' : '+'}${Math.abs(quantityDifference)} units)`,
      severity: 'info'
    });
  };

  const updateOrCreateOrderItem = async (item, productId, orderId) => {
    const { id: itemId, ...itemPayloadWithoutId } = item;
    if (itemId) {
      await axiosInstance.put(`/orders/items/${itemId}`, {
        ...itemPayloadWithoutId,
        product_id: productId,
        order_id: orderId
      });
    } else {
      await axiosInstance.post('/orders/items/', {
        ...itemPayloadWithoutId,
        product_id: productId,
        order_id: orderId
      });
    }
  };

  const handleCancelledOrder = async (currentOrderItems) => {
    let restoredItems = [];
    for (const item of currentOrderItems) {
      await restoreInventory(item);
      restoredItems.push(`${item.product_name} (+${item.quantity} units)`);
    }

    if (restoredItems.length > 0) {
      setSnackbar({
        open: true,
        message: `Order cancelled - Inventory restored: ${restoredItems.join(', ')}`,
        severity: 'info'
      });
    }
  };

  const fetchOrCreateProduct = async (item) => {
    try {
      const productResponse = await axiosInstance.get('/products/', {
        params: { name: item.product_name }
      });

      if (productResponse.data?.length > 0) {
        return productResponse.data[0].id;
      } else {
        const newProductResponse = await axiosInstance.post('/products/', {
          name: item.product_name,
          price: item.unit_price,
          tax: item.tax || 0
        });
        return newProductResponse.data.id;
      }
    } catch (error) {
      console.error('Error fetching or creating product:', error);
      throw error;
    }
  };

  const fetchOrderData = async () => {
    try {
      const orderResponse = await axiosInstance.get(`/orders/${id}`);
      const orderData = orderResponse.data;

      // Fetch account details if we have an account_id
      if (orderData.account_id) {
        try {
          const accountResponse = await axiosInstance.get(`/accounts/${orderData.account_id}`);
          orderData.account_currency = accountResponse.data.currency || 'INR';
          setAccountName(accountResponse.data.name);
        } catch (error) {
          console.error('Error fetching account details:', error);
          orderData.account_currency = 'INR';
        }
      }

      setOrder(orderData);

      if (orderData.sales_order_id) {
        const salesOrderResponse = await axiosInstance.get(`/sales_orders/${orderData.sales_order_id}`);
        setSalesOrder(salesOrderResponse.data);
      }

      if (orderData.purchase_order_id) {
        const purchaseOrderResponse = await axiosInstance.get(`/purchase_orders/${orderData.purchase_order_id}`);
        setPurchaseOrder(purchaseOrderResponse.data);
      }

      if (orderData.deal_id) {
        const dealResponse = await axiosInstance.get(`/deals/${orderData.deal_id}`);
        // Deal data handling if needed
      }

      console.log(`Order and related documents fetched successfully`);
    } catch (error) {
      console.error('Error initializing page:', error);
      if (error.response?.status === 403) {
        setIsUnauthorized(true);
        setSnackbar({
          open: true,
          message: "You don't have permission to access this Orders",
          severity: 'error',
        });
        // Optionally redirect after a delay
        setTimeout(() => {
          navigate('/orders');
        }, 3000);
      } else {
        setSnackbar({
          open: true,
          message: 'Error loading Order data. Please try again later.',
          severity: 'error',
        });
      }
    }
  };

  const getActiveStep = (status) => {
    return orderSteps.indexOf(status);
  };

  const fetchAttachments = async () => {
    const parentTable = 'orders';
    const parentId = id;
    try {
      const response = await axiosInstance.get('/attachments/', {
        params: {
          parent_table: parentTable,
          parent_id: parentId
          // skip: 0,
          // limit: 100,
          // sort_by: 'created_at',
          // sort_order: 'desc'
        }
      });

      setAttachments(response.data || []);

    } catch (error) {
      console.error('Error fetching attachments:', error);
      const errorMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Error fetching attachments';

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 5000
      });
    } finally {
      // If you have a loading state
      // setLoading(false);
    }
  };

  const handleOpenDeleteModal = () => setIsDeleteModalOpen(true);
  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const handleDelete = async () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to delete orders.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    setIsLoading(true);
    handleCloseDeleteModal();
    try {
      // First, delete all order items
      const deleteOrderItemsPromises = orderItems.map(item =>
        axiosInstance.delete(`/orders/items/${item.id}`)
      );

      await Promise.all(deleteOrderItemsPromises); // Wait for all deletions to complete

      // Now delete the order
      await axiosInstance.delete(`/orders/${order.id}`);

      // Navigate to the orders page after deletion
      navigate('/orders'); // Redirect after deletion
      // --- Log order deletion ---
      await frontendLogger.logDelete('Order', {
        id: order.id,
        name: order.order_number,
        deleted_by: user.id,
        user: user.username,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error deleting order:', error);
    } finally {
      setIsLoading(false); // Ensure loading state is reset
    }
  };
  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axiosInstance.delete(`/attachments/${attachmentId}`);
      setAttachments(attachments.filter((attachment) => attachment.id !== attachmentId));
      setSnackbar({
        open: true,
        message: 'Attachment deleted successfully!',
        severity: 'success',
      });
      // --- Log attachment deletion ---
      await frontendLogger.logDelete('Attachment', {
        id: attachmentId,
        order_id: id,
        deleted_by: user.id,
        user: user.username,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting attachment',
        severity: 'error',
      });
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment.bucket_name || !attachment.storage_path) {
        throw new Error('Attachment storage metadata is invalid for strict R2 download');
      }
      await downloadPrivateObject({
        axiosInstance,
        attachmentId: attachment.id,
        fileName: attachment.file_name
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading attachment',
        severity: 'error',
      });
    }
  };

  const handleDealClick = () => {
    navigate(`/deals/${order.deal_id}`); // Redirect to the deal details page
  };

  const handleUploadSuccess = async (uploadData) => {
    // console.log('Upload successful:', uploadData);

    const uploadDataPayload = {
      file_name: uploadData.file.name,
      file_type: uploadData.file.type,
      file_size: uploadData.file.size,
      bucket_name: uploadData.bucket,
      storage_path: uploadData.path,
      description: uploadData.description || '', // Optional, use default if no description is provided
      parent_table: 'orders',  // Assuming the parent table is 'deals', modify as needed
      parent_id: id,  // Assuming the parent ID is the same as lead ID
      is_active: true,
      is_private: uploadData.isPrivate || false, // Default to false if not provided
      file_url: uploadData.url,
      signed_url_expiry: uploadData.signedUrlExpiry || null,
    };

    setIsAttachmentModalOpen(false);
    try {
      const response = await axiosInstance.post(`/attachments/`, uploadDataPayload);

      setSnackbar({
        open: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      });

      // Update files or attachments state as needed
      // setFiles([...files, response.data]); // Uncomment if `files` state exists
      // --- Log attachment creation ---
      await frontendLogger.logCreate('Attachment', {
        id: response.data.id,
        order_id: id,
        file_name: uploadData.file.name,
        file_type: uploadData.file.type,
        file_size: uploadData.file.size,
        user: user.username,
        uploaded_by: user.id,
        uploaded_at: response.data.created_at || new Date().toISOString()
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      setSnackbar({
        open: true,
        message: 'Error uploading file',
        severity: 'error',
      });
    } finally {
      fetchAttachments();
    }
  };

  // const handleCloneOrder = async (payload) => {
  //   try {
  //     // Clean the order payload
  //     const {
  //       id,
  //       order_number,
  //       created_at,
  //       updated_at,
  //       created_by,
  //       updated_by,
  //       created_by_name,
  //       updated_by_name,
  //       ...cleanOrderPayload
  //     } = payload.order;

  //     // Calculate order totals
  //     let subtotal = 0;
  //     let totalTax = 0;

  //     // Pre-process order items to ensure correct calculations
  //     const processedOrderItems = payload.orderItems.map(item => {
  //       const quantity = parseFloat(item.quantity) || 0;
  //       const unitPrice = parseFloat(item.unit_price) || 0;
  //       const discountPercentage = parseFloat(item.discount_percentage) || 0;
  //       const taxRate = parseFloat(item.tax) || 0;

  //       // Calculate item level values
  //       const discountAmount = unitPrice * (discountPercentage / 100);
  //       const discountedPrice = unitPrice - discountAmount;
  //       const itemSubtotal = calculateSubtotal();
  //       const itemTax = itemSubtotal * (taxRate / 100);

  //       // Add to order totals
  //       // subtotal += itemSubtotal;
  //       // totalTax += itemTax;

  //       return {
  //         ...item,
  //         quantity,
  //         unit_price: unitPrice,
  //         discount_percentage: discountPercentage,
  //         tax: taxRate,
  //         discounted_price: discountedPrice.toFixed(2),
  //         total_price: (itemSubtotal + itemTax).toFixed(2)
  //       };
  //     });

  //     // Create new order with cleaned and calculated data
  //     const orderData = {
  //       ...cleanOrderPayload,
  //       status: 'Draft',
  //       payment_status: null,
  //       order_date: null,
  //       account_id: payload.account_id,
  //       account_name: payload.account_name,
  //       deal_id: payload.deal_id,
  //       deal_name: payload.deal_name,
  //       contact_id: payload.contact_id,
  //       contact_name: payload.contact_name,
  //       created_by: payload.user?.id,
  //       updated_by: payload.user?.id,
  //       created_by_name: payload.user?.username,
  //       updated_by_name: payload.user?.username,
  //       // total_amount: payload.total_amount,
  //       // subtotal: payload.subtotal,
  //       total_tax: totalTax.toFixed(2)
  //     };
  //     console.log('Order Data getting from clone is this correct or wrong:', orderData);
  //     // Check inventory availability
  //     if (processedOrderItems.length > 0) {
  //       const inventoryChecks = await Promise.all(
  //         processedOrderItems.map(async (item) => {
  //           try {
  //             let productId = item.product_id;
  //             if (!productId && item.product_name) {
  //               const productResponse = await axiosInstance.get('/products/', {
  //                 params: { name: item.product_name }
  //               });
  //               if (productResponse.data && productResponse.data.length > 0) {
  //                 productId = productResponse.data[0].id;
  //               }
  //             }

  //             if (!productId) {
  //               return {
  //                 item,
  //                 hasInventory: true,
  //                 message: `New product ${item.product_name} will be created`
  //               };
  //             }

  //             const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${productId}`);
  //             const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === productId);

  //             if (!inventoryItem) {
  //               return {
  //                 item,
  //                 hasInventory: false,
  //                 message: `No inventory record found for ${item.product_name}`
  //               };
  //             }

  //             const hasEnoughInventory = inventoryItem.quantity >= item.quantity;
  //             return {
  //               item,
  //               hasInventory: hasEnoughInventory,
  //               currentStock: inventoryItem.quantity,
  //               requiredQuantity: item.quantity,
  //               message: hasEnoughInventory
  //                 ? `Sufficient inventory for ${item.product_name}`
  //                 : `Insufficient inventory for ${item.product_name} (${inventoryItem.quantity} available, ${item.quantity} required)`
  //             };
  //           } catch (error) {
  //             throw new Error(`Inventory check failed for ${item.product_name}: ${error.message}`);
  //           }
  //         })
  //       );

  //       const insufficientItems = inventoryChecks.filter(check => !check.hasInventory);
  //       if (insufficientItems.length > 0) {
  //         const messages = insufficientItems.map(item => item.message);
  //         throw new Error(`Inventory check failed:\n${messages.join('\n')}`);
  //       }
  //     }

  //     // Create the cloned order
  //     const clonedOrderResponse = await axiosInstance.post('/orders/', orderData);

  //     // Process order items
  //     if (processedOrderItems.length > 0) {
  //       await Promise.all(processedOrderItems.map(async (item) => {
  //         try {
  //           // Get or create product
  //           let productId = item.product_id;
  //           if (!productId && item.product_name) {
  //             const productResponse = await axiosInstance.get('/products/', {
  //               params: { name: item.product_name }
  //             });

  //             if (productResponse.data && productResponse.data.length > 0) {
  //               productId = productResponse.data[0].id;
  //             } else {
  //               const newProduct = await axiosInstance.post('/products/', {
  //                 name: item.product_name,
  //                 price: item.unit_price,
  //                 tax: item.tax || 0
  //               });
  //               productId = newProduct.data.id;
  //             }
  //           }

  //           // Create order item with accurate calculations
  //           await axiosInstance.post('/orders/items/', {
  //             product_id: productId,
  //             order_id: clonedOrderResponse.data.id,
  //             product_name: item.product_name,
  //             quantity: item.quantity,
  //             unit_price: item.unit_price,
  //             discount_percentage: item.discount_percentage,
  //             tax: item.tax,
  //             discounted_price: item.discounted_price,
  //             total_price: item.total_price
  //           });

  //           // Update inventory
  //           const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${productId}`);
  //           const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === productId);

  //           if (inventoryItem) {
  //             const newQuantity = inventoryItem.quantity - item.quantity;
  //             await axiosInstance.put(`/inventory/${inventoryItem.id}`, {
  //               ...inventoryItem,
  //               quantity: newQuantity
  //             });
  //           }
  //         } catch (error) {
  //           throw new Error(`Failed to process item ${item.product_name}: ${error.message}`);
  //         }
  //       }));
  //     }

  //     navigate(`/orders/${clonedOrderResponse.data.id}`);
  //     setSnackbar({
  //       open: true,
  //       message: `Order cloned successfully! New order number: ${clonedOrderResponse.data.order_number}`,
  //       severity: 'success'
  //     });
  //     setIsCloneModalOpen(false);

  //     return clonedOrderResponse.data;

  //   } catch (error) {
  //     console.error('Clone Order Error:', {
  //       message: error.message,
  //       response: error.response?.data
  //     });

  //     setSnackbar({
  //       open: true,
  //       message: error.message || 'Failed to clone order',
  //       severity: 'error'
  //     });

  //     throw error;
  //   }
  // };


  const handleCloneOrder = async (payload) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to clone orders.',
        severity: 'error',
      });
      return; // Exit the function early
    }

    try {
      // Clean the order payload
      const {
        id,
        order_number,
        created_at,
        updated_at,
        created_by,
        updated_by,
        created_by_name,
        updated_by_name,
        ...cleanOrderPayload
      } = payload.order;

      // Calculate order totals
      let subtotal = 0;
      let totalTax = 0;

      // Pre-process order items to ensure correct calculations
      const processedOrderItems = payload.orderItems.map(item => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercentage = parseFloat(item.discount_percentage) || 0;
        const taxRate = parseFloat(item.tax) || 0;

        // Calculate item level values
        const discountAmount = unitPrice * (discountPercentage / 100);
        const discountedPrice = unitPrice - discountAmount;
        const itemSubtotal = discountedPrice * quantity;
        const itemTax = itemSubtotal * (taxRate / 100);

        return {
          ...item,
          quantity,
          unit_price: unitPrice,
          discount_percentage: discountPercentage,
          tax: taxRate,
          discounted_price: discountedPrice.toFixed(2),
          total_price: (itemSubtotal + itemTax).toFixed(2)
        };
      });

      // Create new order with cleaned and calculated data
      const today = new Date().toISOString().split('T')[0];
      const orderData = {
        ...cleanOrderPayload,
        status: 'Draft',
        payment_status: null,
        order_date: payload.order_date
          ? new Date(payload.order_date).toISOString().split('T')[0]
          : today,
        delivery_date: null, // Always set to null unless user sets it
        account_id: payload.account_id,
        account_name: payload.account_name,
        deal_id: payload.deal_id,
        deal_name: payload.deal_name,
        contact_id: payload.contact_id,
        contact_name: payload.contact_name,
        created_by: payload.user?.id,
        updated_by: payload.user?.id,
        created_by_name: payload.user?.username,
        updated_by_name: payload.user?.username,
        total_tax: totalTax.toFixed(2)
      };
      console.log('Order Data getting from clone is this correct or wrong:', orderData);

      // Check inventory availability - ONLY for physical products
      if (processedOrderItems.length > 0) {
        const inventoryChecks = await Promise.all(
          processedOrderItems.map(async (item) => {
            try {
              let productId = item.product_id;
              if (!productId && item.product_name) {
                const productResponse = await axiosInstance.get('/products/', {
                  params: { name: item.product_name }
                });
                if (productResponse.data && productResponse.data.length > 0) {
                  productId = productResponse.data[0].id;
                }
              }

              if (!productId) {
                return {
                  item,
                  hasInventory: true,
                  message: `New product ${item.product_name} will be created`
                };
              }

              // Check product type first
              const productResponse = await axiosInstance.get(`/products/${productId}`);
              const product = productResponse.data;

              // Skip inventory check for non-physical products
              if (product.product_type !== 'Physical') {
                console.log(`Skipping inventory check for non-physical product: ${item.product_name}`);
                return {
                  item,
                  hasInventory: true,
                  message: `Skipping inventory check for ${product.product_type} product: ${item.product_name}`
                };
              }

              const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${productId}`);
              const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === productId);

              if (!inventoryItem) {
                return {
                  item,
                  hasInventory: false,
                  message: `No inventory record found for ${item.product_name}`
                };
              }

              const hasEnoughInventory = inventoryItem.quantity >= item.quantity;
              return {
                item,
                hasInventory: hasEnoughInventory,
                currentStock: inventoryItem.quantity,
                requiredQuantity: item.quantity,
                message: hasEnoughInventory
                  ? `Sufficient inventory for ${item.product_name}`
                  : `Insufficient inventory for ${item.product_name} (${inventoryItem.quantity} available, ${item.quantity} required)`
              };
            } catch (error) {
              throw new Error(`Inventory check failed for ${item.product_name}: ${error.message}`);
            }
          })
        );

        const insufficientItems = inventoryChecks.filter(check => !check.hasInventory);
        if (insufficientItems.length > 0) {
          const messages = insufficientItems.map(item => item.message);
          throw new Error(`Inventory check failed:\n${messages.join('\n')}`);
        }
      }

      // Create the cloned order
      const clonedOrderResponse = await axiosInstance.post('/orders/', orderData);

      // Process order items
      if (processedOrderItems.length > 0) {
        await Promise.all(processedOrderItems.map(async (item) => {
          try {
            // Get or create product
            let productId = item.product_id;
            if (!productId && item.product_name) {
              const productResponse = await axiosInstance.get('/products/', {
                params: { name: item.product_name }
              });

              if (productResponse.data && productResponse.data.length > 0) {
                productId = productResponse.data[0].id;
              } else {
                const newProduct = await axiosInstance.post('/products/', {
                  name: item.product_name,
                  price: item.unit_price,
                  tax: item.tax || 0
                });
                productId = newProduct.data.id;
              }
            }

            // Get product type before updating inventory
            const productResponse = await axiosInstance.get(`/products/${productId}`);
            const product = productResponse.data;

            // For non-physical products, ensure quantity is 1
            let quantity = item.quantity;
            if (product.product_type === 'Service' || product.product_type === 'Digital') {
              if (quantity !== 1) {
                quantity = 1;
                console.log(`Set quantity to 1 for non-physical product: ${item.product_name}`);
              }
            }

            // Create order item with accurate calculations
            await axiosInstance.post('/orders/items/', {
              product_id: productId,
              order_id: clonedOrderResponse.data.id,
              product_name: item.product_name,
              quantity: quantity,
              unit_price: item.unit_price,
              discount_percentage: item.discount_percentage,
              tax: item.tax,
              discounted_price: item.discounted_price,
              total_price: item.total_price
            });

            // Update inventory only for physical products
            if (product.product_type === 'Physical') {
              const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${productId}`);
              const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === productId);

              if (inventoryItem) {
                const newQuantity = inventoryItem.quantity - quantity;
                await axiosInstance.put(`/inventory/${inventoryItem.id}`, {
                  ...inventoryItem,
                  quantity: newQuantity
                });
                console.log(`Updated inventory for physical product: ${item.product_name} (-${quantity} units)`);
              }
            } else {
              console.log(`Skipping inventory update for non-physical product: ${item.product_name}`);
            }
          } catch (error) {
            throw new Error(`Failed to process item ${item.product_name}: ${error.message}`);
          }
        }));
      }

      navigate(`/orders/${clonedOrderResponse.data.id}`);
      setSnackbar({
        open: true,
        message: `Order cloned successfully! New order number: ${clonedOrderResponse.data.order_number}`,
        severity: 'success'
      });
      setIsCloneModalOpen(false);

      return clonedOrderResponse.data;

    } catch (error) {
      console.error('Clone Order Error:', {
        message: error.message,
        response: error.response?.data
      });

      setSnackbar({
        open: true,
        message: error.message || 'Failed to clone order',
        severity: 'error'
      });

      throw error;
    }
  };

  const handleOpenActivityModal = () => {
    setIsActivityModalOpen(true);
  };

  const handleCloseActivityModal = () => {
    setIsActivityModalOpen(false);
    setNewActivity({
      order_id: id,
      type: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
  };

  const handleActivitySaved = async (newActivityData) => {
    setIsActivitiesLoading(true);
    try {
      const currentDate = new Date().toISOString();
      setActivities([...activities, newActivityData]);

      const lastActivity = await axiosInstance.patch(`/orders/${id}`, {
        last_activity: currentDate,
      });

      // console.log('Last activity updated:', lastActivity.data);

      setSnackbar({
        open: true,
        message: 'Activity created successfully!',
        severity: 'success',
      });

      await fetchOrderData(); // Refresh lead data to get updated information
    } catch (error) {
      console.error('Error updating last activity:', error);
      setSnackbar({
        open: true,
        message: 'Error updating last activity',
        severity: 'error',
      });
    } finally {
      setIsActivitiesLoading(false);
    }
  };

  const handleActivityInputChange = (e) => {
    setNewActivity({ ...newActivity, [e.target.name]: e.target.value });
  };

  const handleSaveActivity = async () => {
    setIsLoading(true);
    handleCloseActivityModal();
    try {
      const response = await axiosInstance.post(`/orders/${id}/activities/`, newActivity);
      setActivities([...activities, response.data]);
      setSnackbar({
        open: true,
        message: 'Activity created successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error saving activity:', error);
      setSnackbar({
        open: true,
        message: 'Error saving activity',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    setIsLoading(true);
    try {
      await axiosInstance.delete(`/activities/${activityId}`);
      setActivities(activities.filter((activity) => activity.id !== activityId));
      setSnackbar({
        open: true,
        message: 'Activity deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting activity:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting activity',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };



  const handleEditActivity = (updated) => {
    if (!updated?.id) return;
    setActivities((prev) =>
      prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
    );
  };

  const fetchActivities = async () => {
    if (!hasTasksModule) {
      setActivities([]);
      return;
    }
    console.log('fetchActivities function called');
    // console.log('ID value:', id); // Log the `id` to ensure it has the correct value.

    try {
      const params = new URLSearchParams();

      // Log when parameters are being appended.
      if (true) {
        // console.log('Appending "parent_table" to params with value "orders"');
        params.append('parent_table', 'orders');
      }
      if (true) {
        // console.log(`Appending "parent_table_id" to params with value "${id}"`);
        params.append('parent_table_id', id);
      }

      // console.log('URLSearchParams created:', params.toString());

      // Uncomment if using the commented axios call for testing.
      // console.log('Sending request to `/activities/` with params:', params.toString());

      // Sending request to endpoint
      // console.log(`Sending GET request to /activities/orders/${id}`);
      const response = await axiosInstance.get('/activities/', {
        params: {
          parent_table: 'orders',
          parent_table_id: id
        }
      });
      // Log the response status and data
      // console.log('Response status:', response.status);
      // console.log('Response data:', response.data);

      // Ensure data is valid before setting
      setActivities(response.data || []);
      // console.log('Activities state updated successfully');
    } catch (error) {
      if (isModuleNotEntitledError(error)) {
        setActivities([]);
        return;
      }
      // Log detailed error information
      console.error('Error occurred during fetchActivities execution:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        config: error.config,
        response: error.response,
      });

      // Check if response is defined and log its details
      if (error.response) {
        console.error('Error response details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
        });
      } else {
        console.error('No response received. Possible network error.');
      }

      // Show error notification
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, 'Error fetching activities'),
        severity: 'error',
      });

      // Clear activities state on error
      setActivities([]);
    }
  };



  const handleInvoiceClick = async () => {
    setInvoicePercent(100);
    setInvoiceAmount('');
    setSplitMode('percentage');
    setInvoicePercentError('');
    setIsInvoiceDialogOpen(true);
  };

  // Handle dialog close
  const handleCloseInvoiceDialog = () => {
    setIsInvoiceDialogOpen(false);
    setInvoicePercentError('');
  };

  const roundMoney = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

  const getInvoiceSplitTotals = () => {
    const orderTotal = roundMoney(order?.total_amount || 0);
    const storedInvoiced = roundMoney(order?.invoiced_amount || 0);
    const storedRemaining = roundMoney(
      order?.remaining_amount !== undefined && order?.remaining_amount !== null
        ? order.remaining_amount
        : Math.max(0, orderTotal - storedInvoiced)
    );

    let displayInvoiced = storedInvoiced;
    let displayRemaining = storedRemaining;

    // Keep summary consistent when stored fields drift (e.g. total changed after partial invoices)
    if (Math.abs(storedInvoiced + storedRemaining - orderTotal) > 0.05) {
      if (storedInvoiced === 0 && storedRemaining > 0 && storedRemaining < orderTotal - 0.05) {
        displayInvoiced = roundMoney(orderTotal - storedRemaining);
        displayRemaining = storedRemaining;
      } else {
        displayRemaining = roundMoney(Math.max(0, orderTotal - storedInvoiced));
      }
    }

    return {
      orderTotal,
      displayInvoiced,
      displayRemaining,
      remainingPercent: orderTotal > 0 ? (displayRemaining / orderTotal) * 100 : 0,
      invoicedPercent: orderTotal > 0 ? (displayInvoiced / orderTotal) * 100 : 0,
    };
  };

  // Handle dialog confirm
  const handleConfirmInvoiceDialog = async () => {
    // Check for existing invoices and their types
    const hasFullInvoice = invoices.some(inv => inv.invoice_type === 'full');
    const hasPartialInvoices = invoices.some(inv => inv.invoice_type === 'partial');
    const totalInvoicedPercent = invoices.reduce((total, inv) => total + (inv.percentage || 0), 0);

    const EPSILON = 0.01; // for floating point comparison
    const { orderTotal, displayRemaining, remainingPercent } = getInvoiceSplitTotals();

    let percent = 0;
    let amountToInvoice = 0;
    let isLastSlice = false;

    if (splitMode === 'percentage') {
      if (isNaN(invoicePercent) || invoicePercent <= 0 || invoicePercent > 100) {
        setInvoicePercentError('Please enter a valid percentage (1-100)');
        return;
      }
      if (invoicePercent > remainingPercent + EPSILON) {
        setInvoicePercentError(`Cannot invoice more than ${remainingPercent.toFixed(2)}% remaining`);
        return;
      }
      isLastSlice = Math.abs(invoicePercent - remainingPercent) <= EPSILON || invoicePercent >= remainingPercent;
      percent = isLastSlice ? remainingPercent : Number(invoicePercent);
      amountToInvoice = isLastSlice
        ? displayRemaining
        : roundMoney((orderTotal * percent) / 100);
    } else {
      const amount = roundMoney(invoiceAmount);
      if (isNaN(amount) || amount <= 0) {
        setInvoicePercentError('Please enter a valid amount (not more than remaining)');
        return;
      }
      if (amount > displayRemaining + EPSILON) {
        setInvoicePercentError('Please enter a valid amount (not more than remaining)');
        return;
      }
      isLastSlice = Math.abs(amount - displayRemaining) <= EPSILON || amount >= displayRemaining;
      amountToInvoice = isLastSlice ? displayRemaining : amount;
      percent = orderTotal > 0 ? (amountToInvoice / orderTotal) * 100 : 0;
    }

    const isFullByPercent = splitMode === 'percentage' && Math.abs(percent - 100) <= EPSILON;
    const isFullByAmount = splitMode === 'amount' && Math.abs(amountToInvoice - orderTotal) <= EPSILON && displayRemaining >= orderTotal - EPSILON;
    const isTryingFullInvoice = isFullByPercent || isFullByAmount;
    const shouldPreventFullInvoice = isTryingFullInvoice && hasPartialInvoices && totalInvoicedPercent < 100;

    if (shouldPreventFullInvoice) {
      setInvoicePercentError('Please enter a valid amount (not more than remaining)');
      return;
    }

    // Only proceed if all validations pass
    setInvoicePercentError('');

    try {
      await handleGenerateInvoiceWithBreakdown({
        percent,
        amount: amountToInvoice,
        isLastSlice,
      });
      // Only close dialog if invoice generation was successful
      setIsInvoiceDialogOpen(false);
    } catch (error) {
      // Don't close dialog if there was an error
      console.error('Error generating invoice:', error);
    }
  };

  // Generate invoice with breakdown
  const handleGenerateInvoiceWithBreakdown = async (splitOverride) => {
    try {
      setIsInvoiceButtonLoading(true);
      const { orderTotal, displayRemaining } = getInvoiceSplitTotals();
      const percent = typeof splitOverride === 'object'
        ? Number(splitOverride.percent)
        : Number(splitOverride !== undefined ? splitOverride : invoicePercent);
      const invoiceTotal = typeof splitOverride === 'object' && splitOverride.amount !== undefined
        ? roundMoney(splitOverride.amount)
        : roundMoney((orderTotal * percent) / 100);
      const scale = orderTotal > 0 ? invoiceTotal / orderTotal : 0;

      // Prepare invoice items payload, scale quantities/prices if needed
      const invoiceItemsPayload = order.order_items.map((item) => {
        if (!item.product_id || !item.product_name) return null;
        return {
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity * scale,
          unit_price: item.unit_price,
          tax: item.tax || 0,
          discount_percentage: item.discount_percentage || 0,
          total_price: (item.total_price || (item.quantity * item.unit_price)) * scale,
        };
      }).filter(Boolean);
      if (invoiceItemsPayload.length === 0) throw new Error('No valid invoice items found');
      // Extract transport details from order
      const transportDetails = {
        transid: order.transid || '',
        transname: order.transname || '',
        transmode: order.transmode || '',
        vehno: order.vehno || '',
        vehtype: order.vehtype || '',
        transdocno: order.transdocno || '',
        ...(order.transdocdate ? { transdocdate: order.transdocdate } : {}),
      };
      const isFullInvoice = Math.abs(percent - 100) < 0.01 && Math.abs(displayRemaining - orderTotal) < 0.01;
      // Prepare invoice payload
      const invoicePayload = {
        order_id: order.id,
        sales_order_id: order.id,
        order_number: order.order_number,
        total_amount: invoiceTotal,
        subtotal: invoiceTotal, // For simplicity, backend will recalc
        tax: order.tax || 0,
        discount: order.discount || 0,
        currency: order.currency || 'USD',
        notes: order.shipping_instruction || '',
        status: 'Draft',
        billing_address: order.billing_address || '',
        shipping_address: order.shipping_address || '',
        account_id: order.account_id,
        account_name: order.account_name,
        account_currency: order.account_currency,
        deal_id: order.deal_id,
        items: invoiceItemsPayload,
        invoice_type: isFullInvoice ? 'full' : 'partial',
        percentage: Math.round(percent * 10000) / 10000,
        ...transportDetails
      };
      // Debug: Log the invoice payload before sending
      console.log('[OrderDetail] Invoice creation payload:', invoicePayload);
      const invoiceResponse = await axiosInstance.post('/invoices/', invoicePayload);
      setSnackbar({ open: true, message: 'Invoice generated successfully!', severity: 'success' });
      fetchInvoices();
      await fetchOrderData(); // Refresh order data after invoice
    } catch (error) {
      let errorMessage = error?.response?.data?.detail || error?.message || 'Error generating invoice';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      // Re-throw the error so it can be caught by the parent function
      throw error;
    } finally {
      setIsInvoiceButtonLoading(false);
    }
  };

  // Helper function to calculate subtotal if not already present
  const calculateSubtotal = () => {
    // const subtotal = order.order_items.reduce((total, item) =>
    //     total + ((item.quantity || 1) * (item.unit_price || 0)), 0);
    const subtotal = orderItems.reduce((total, item) => total + parseFloat(item.total_price || 0), 0); // Updated line
    console.log('Calculated Subtotal:', subtotal);
    return subtotal;
  };

  // Helper function to calculate tax if not already present
  const calculateTax = () => {
    const tax = orderItems.reduce((total, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unit_price) || 0;
      const discountPercentage = parseFloat(item.discount_percentage) || 0;
      const taxRate = parseFloat(item.tax) || 0;
      const discountedPrice = unitPrice - (unitPrice * (discountPercentage / 100));
      return total + (discountedPrice * quantity * (taxRate / 100));
    }, 0);
    console.log('Calculated Tax:', tax);
    return tax;
  };

  useEffect(() => {
    if (id) {
      fetchInvoices();
    }
  }, [id]);

  // Fetch Invoices Function
  const fetchInvoices = async () => {
    setIsInvoicesLoading(true);
    try {
      const response = await axiosInstance.get(`/invoices/?sales_order_id=${id}`);
      // Filter invoices to ensure they belong to the current order
      const filteredInvoices = response.data.filter(
        (invoice) => invoice.sales_order_id === parseInt(id)
      );
      setInvoices(filteredInvoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setIsInvoicesLoading(false);
    }
  };



  // const handleEditInvoice = async (invoiceId) => {
  //   try {
  //     // Fetch full invoice details
  //     const invoiceResponse = await axiosInstance.get(`/invoices/${invoiceId}/`);

  //     // Fetch invoice items, ensuring you get the most recent data
  //     const invoiceItemsResponse = await axiosInstance.get(`/invoices/${invoiceId}/items/`);

  //     setEditedInvoice({
  //       ...invoiceResponse.data,
  //       invoice_date: invoiceResponse.data.invoice_date
  //         ? new Date(invoiceResponse.data.invoice_date).toISOString().split('T')[0]
  //         : null,
  //       due_date: invoiceResponse.data.due_date
  //         ? new Date(invoiceResponse.data.due_date).toISOString().split('T')[0]
  //         : null,
  //     });

  //     // Ensure items are fresh and not marked for deletion
  //     setEditedInvoiceItems(
  //       invoiceItemsResponse.data.map(item => ({
  //         ...item,
  //         markedForDeletion: false
  //       }))
  //     );

  //     setOpenEditInvoiceModal(true);
  //   } catch (error) {
  //     console.error("Error fetching invoice details:", error);
  //   }
  // };

  const handleEditInvoice = async (invoiceId) => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to edit orders.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    try {
      setIsEditInvoiceLoading(true);

      // Fetch full invoice details
      const invoiceResponse = await axiosInstance.get(`/invoices/${invoiceId}/`);

      // Fetch invoice items, ensuring you get the most recent data
      const invoiceItemsResponse = await axiosInstance.get(`/invoices/${invoiceId}/items/`);

      setEditedInvoice({
        ...invoiceResponse.data,
        invoice_date: invoiceResponse.data.invoice_date
          ? new Date(invoiceResponse.data.invoice_date).toISOString().split('T')[0]
          : null,
        due_date: invoiceResponse.data.due_date
          ? new Date(invoiceResponse.data.due_date).toISOString().split('T')[0]
          : null,
      });

      // Ensure items are fresh and not marked for deletion
      setEditedInvoiceItems(
        invoiceItemsResponse.data.map(item => ({
          ...item,
          markedForDeletion: false
        }))
      );

      setOpenEditInvoiceModal(true);
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      enqueueSnackbar('Failed to load invoice details', { variant: 'error' });
    } finally {
      setIsEditInvoiceLoading(false);
    }
  };

  const handleInvoiceFieldChange = (field, value) => {
    setEditedInvoice(prev => {
      const updates = { ...prev, [field]: value };

      // If changing account, update related fields
      if (field === 'account_id') {
        const selectedAccount = accounts.find(acc => acc.value === value);
        if (selectedAccount) {
          updates.account_name = selectedAccount.label;
          updates.account_currency = selectedAccount.currency;
        }
      }

      return updates;
    });
  };

  const handleInvoiceItemChange = (tempId, field, value) => {
    const updatedItems = [...editedInvoiceItems];
    const itemIndex = updatedItems.findIndex(item => item.tempId === tempId);
    if (itemIndex === -1) return;

    const item = updatedItems[itemIndex];
    if (field === "discount_percentage") {
      if (value > 100) value = 100;
      else if (value < 0) value = 0;
    }

    if (field === 'product_name') {
      const selectedProduct = productList.find(p => p.name === value);
      if (selectedProduct) {
        item.unit_price = selectedProduct.price;
        item.tax = selectedProduct.tax_rate;
        item.product_id = selectedProduct.id;
        item.discounted_price = item.unit_price - (item.unit_price * item.discount_percentage / 100);
      }
    }

    item[field] = field === 'product_name' ? value : (value === '' ? 0 : parseFloat(value));
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const discountPercentage = parseFloat(item.discount_percentage) || 0;
    const taxRate = parseFloat(item.tax) || 0;
    const discountedPrice = unitPrice * (1 - discountPercentage / 100);
    const itemNet = discountedPrice * quantity;
    item.discounted_price = discountedPrice;
    item.total_price = itemNet * (1 + taxRate / 100);

    setEditedInvoiceItems(updatedItems);
  };

  const handleAddInvoiceItem = () => {
    const newItem = {
      product_name: '',
      unit_price: 0,
      quantity: 1,
      discount_percentage: 0,
      tax: 0,
      total_price: 0,
      discounted_price: 0,
      markedForDeletion: false,
      tempId: Date.now() + Math.random() // Unique temporary ID
    };
    setEditedInvoiceItems(prev => [...prev, newItem]);
  };

  // Update handleSaveInvoice to filter out blank rows and handle deletions


  const handleSaveInvoice = async () => {
    try {
      setIsLoading(true);

      // Filter out blank rows (rows with no product_name) and marked deletions
      const validItems = editedInvoiceItems.filter(item =>
        !item.markedForDeletion &&
        item.product_name &&
        item.product_name.trim() !== ''
      );

      if (validItems.length === 0) {
        enqueueSnackbar('At least one valid item is required', { variant: 'error' });
        return;
      }

      // Initialize subtotal and tax summary (original code)
      let subtotal = 0;
      const taxSummary = {};

      // Calculate subtotal and tax (original calculation with valid items)
      validItems.forEach(item => {
        // Parse all values to ensure numbers
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercentage = parseFloat(item.discount_percentage) || 0;
        const taxRate = parseFloat(item.tax) || 0;

        // Calculate discount
        const discountAmount = unitPrice * (discountPercentage / 100);
        const discountedPrice = unitPrice - discountAmount;

        // Calculate item's subtotal (before tax)
        const itemSubtotal = discountedPrice * quantity;
        subtotal += itemSubtotal;

        // Tax on discounted net (after discount).
        const itemTaxAmount = itemSubtotal * (taxRate / 100);

        // Accumulate tax amounts in tax summary
        if (taxRate > 0) {
          if (!taxSummary[taxRate]) {
            taxSummary[taxRate] = { taxRate, totalAmount: 0 };
          }
          taxSummary[taxRate].totalAmount += itemTaxAmount;
        }
      });

      // Round subtotal to 2 decimal places
      subtotal = parseFloat(subtotal.toFixed(2));

      // Calculate total tax (sum of taxSummary values)
      const totalTax = Object.values(taxSummary).reduce((acc, { totalAmount }) => acc + parseFloat(totalAmount.toFixed(2)), 0);

      // Calculate grand total (subtotal + total tax)
      const grandTotal = parseFloat((subtotal + totalTax).toFixed(2));

      console.log('Calculated values:', { subtotal, totalTax, grandTotal, taxSummary });

      // Prepare invoice payload (original structure maintained)
      const invoicePayload = {
        id: editedInvoice.id,
        invoice_number: editedInvoice.invoice_number,
        invoice_date: editedInvoice.invoice_date,
        due_date: editedInvoice.due_date,
        status: editedInvoice.status,
        notes: editedInvoice.notes || '',
        subtotal,
        total_amount: grandTotal,
        total_tax: totalTax,
        currency: editedInvoice.currency,
        account_id: editedInvoice.account_id,
        account_name: editedInvoice.account_name,
        // Add transport details
        transid: editedInvoice.transid || '',
        transname: editedInvoice.transname || '',
        transmode: editedInvoice.transmode || '',
        vehno: editedInvoice.vehno || '',
        vehtype: editedInvoice.vehtype || '',
      };

      // Add tax slab information (original code)
      Object.entries(taxSummary).forEach(([taxRate, details], index) => {
        const slabIndex = index + 1;
        invoicePayload[`tax_slab_${slabIndex}`] = parseFloat(details.taxRate);
        invoicePayload[`tax_slab_amount_${slabIndex}`] = parseFloat(details.totalAmount.toFixed(2));
      });

      console.log('Sending invoice payload:', invoicePayload);

      // Update invoice
      await axiosInstance.put(`/invoices/${editedInvoice.id}/`, invoicePayload);

      // Delete items marked for deletion (new addition)
      const itemsToDelete = editedInvoiceItems.filter(item => item.id && item.markedForDeletion);
      for (const item of itemsToDelete) {
        await axiosInstance.delete(`/invoices/items/${item.id}/`);
      }

      // Update or create invoice items (original code with valid items)
      for (const item of validItems) {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercentage = parseFloat(item.discount_percentage) || 0;
        const taxRate = parseFloat(item.tax) || 0;

        // Calculate item values
        const discountAmount = unitPrice * (discountPercentage / 100);
        const discountedPrice = parseFloat((unitPrice - discountAmount).toFixed(2));
        const itemSubtotal = parseFloat((discountedPrice * quantity).toFixed(2));

        // Tax on discounted net (after discount).
        const taxAmount = parseFloat((itemSubtotal * (taxRate / 100)).toFixed(2));

        const totalPrice = parseFloat((itemSubtotal + taxAmount).toFixed(2));

        const itemPayload = {
          product_id: item.product_id,
          product_name: item.product_name,
          quantity,
          unit_price: unitPrice,
          discount_percentage: discountPercentage,
          tax: taxRate,
          total_price: totalPrice,
          discounted_price: discountedPrice,
        };

        if (item.id) {
          await axiosInstance.put(`/invoices/items/${item.id}/`, itemPayload);
        } else {
          await axiosInstance.post(`/invoices/${editedInvoice.id}/items/`, itemPayload);
        }
      }

      // Refresh invoices list
      await fetchInvoices();
      enqueueSnackbar('Invoice updated successfully', { variant: 'success' });

    } catch (error) {
      console.error('Error saving invoice:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      enqueueSnackbar('Failed to save invoice', { variant: 'error' });
    } finally {
      setIsLoading(false);
      setOpenEditInvoiceModal(false);
    }
  };

  const calculateTotalAmount = () => {
    let subtotal = 0;
    let taxSummary = {}; // Object to store tax rates and their total amounts

    editedInvoiceItems.forEach((item) => {
      const unitPrice = parseFloat(item.unit_price) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const discountPercentage = parseFloat(item.discount_percentage) || 0;
      const taxRate = parseFloat(item.tax) || 0;

      // Calculate discounted price (if needed for subtotal)
      const discountedPrice = unitPrice - (unitPrice * (discountPercentage / 100));
      const totalDiscountedPrice = discountedPrice * quantity;

      // Add to subtotal
      subtotal += totalDiscountedPrice;

      // Tax on discounted net (after discount).
      const taxAmount = totalDiscountedPrice * (taxRate / 100);

      // Update tax summary
      if (taxRate) {
        taxSummary[taxRate] = (taxSummary[taxRate] || 0) + taxAmount;
      }

      // Update item with calculated values
      item.discounted_price = discountedPrice;
      item.total_discounted_price = totalDiscountedPrice;
      item.tax_amount = (totalDiscountedPrice * (taxRate / 100));
      item.total_price = totalDiscountedPrice + item.tax_amount; // Total price including tax
    });

    // Calculate total tax
    const totalTax = Object.values(taxSummary).reduce((acc, taxAmount) => acc + taxAmount, 0);

    // Grand total is subtotal + total tax
    const grandTotal = subtotal + totalTax;

    return { subtotal, grandTotal, taxSummary }; // Return subtotal, grand total, and tax summary
  };


  const handleRemoveOrderItem = (index) => {
    const updatedItems = editedInvoiceItems.filter((_, i) => i !== index);
    setEditedInvoiceItems(updatedItems);
  };
  const isEditable = editedInvoice?.status === 'Draft';

  // Add these state declarations at the top of the component
  const [formErrors, setFormErrors] = useState({});
  const [updatedFields, setUpdatedFields] = useState({});

  // Add useEffect to monitor form errors
  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      console.log('Form errors updated:', formErrors);
    }
  }, [formErrors]);

  // Update handleRemoveInvoiceItem to properly handle row removal
  const handleRemoveInvoiceItem = (index) => {
    // Count active (non-deleted) items
    const activeItems = editedInvoiceItems.filter(item => !item.markedForDeletion);

    // Prevent removal if only one active item remains
    if (activeItems.length <= 1) {
      enqueueSnackbar('At least one item is required in the invoice', { variant: 'warning' });
      return;
    }

    const updatedItems = [...editedInvoiceItems];
    const itemToRemove = updatedItems[index];

    if (itemToRemove.id) {
      // For existing items, mark for deletion
      updatedItems[index] = { ...itemToRemove, markedForDeletion: true };
    } else {
      // For new items (no id), remove directly
      updatedItems.splice(index, 1);
    }

    setEditedInvoiceItems(updatedItems);
  };


  // Add these handler functions
  const handleFieldUpdate = (field, value) => {
    setUpdatedFields((prev) => {
      let updatedValue = value;

      // Handle custom fields (fields starting with cf_)
      if (field.startsWith('cf_')) {
        // Handle empty values
        if (value === '' || value === undefined) {
          updatedValue = null;
        }
        // Handle number fields - allow 0 values
        else if (typeof value === 'string' && !isNaN(value)) {
          const numValue = parseFloat(value);
          updatedValue = Number.isFinite(numValue) ? Number(numValue.toFixed(2)) : value;
        }
        // Handle boolean values
        else if (value === true || value === false) {
          updatedValue = value;
        }
        // Handle string values
        else if (value === null || typeof value === 'string') {
          updatedValue = value;
        }
      }

      const updated = { ...prev, [field]: updatedValue };

      // Only clear errors if the field previously had an error
      if (formErrors[field]) {
        setFormErrors((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
      }

      return updated;
    });
  };

  const handleFieldError = (field, error) => {
    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else if (formErrors[field]) {
      setFormErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // In your rendering logic:
  if (isLoading || !order) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          title="Loading Order Data"
          message="Please wait while we fetch the order information..."
        />
        
      </DashboardLayout>
    );
  }

  // Add custom fields section renderer
  // const renderCustomFields = () => (
  //   <CustomFieldsSection
  //     ref={customFieldsRef}
  //     objectType="orders"
  //     objectData={order}
  //     // isEditing={isEditable}
  //     onFieldUpdate={(field, value) => {
  //       handleFieldUpdate(field, value);
  //     }}
  //     onError={(field, error) => {
  //       if (error) {
  //         handleFieldError(field, error);
  //       }
  //     }}
  //     formErrors={formErrors}
  //   />
  // );

  // Add these functions
  const toggleCc = () => setShowCc(!showCc);
  const toggleBcc = () => setShowBcc(!showBcc);

  const handleQuillChange = (content) => {
    setNewEmail({ ...newEmail, content });
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link', 'image',
  ];

  const handleToggleEmailModal = async () => {
    if (!isEmailModalOpen) {
      if (!order) {
        setSnackbar({
          open: true,
          message: 'Order details not loaded. Please try again.',
          severity: 'error',
        });
        return;
      }

      setIsEmailButtonLoading(true);
      try {
        // Fetch account details to get email
        let accountEmail = '';
        if (order.account_id) {
          const accountResponse = await axiosInstance.get(`/accounts/${order.account_id}`);
          accountEmail = accountResponse.data.email;
        }

        // Fetch contact details to get email (if contact_id exists)
        let contactEmail = '';
        if (order.contact_id) {
          const contactResponse = await axiosInstance.get(`/contacts/${order.contact_id}`);
          contactEmail = contactResponse.data.email;
        }

        // Use contact email first, fallback to account email
        const recipientEmail = contactEmail || accountEmail || '';

        const emailSubject = `Order ${order.order_number} - Information`;
        let emailContent = `\n           `;

        // --- Fetch Order PDF as Blob and set as attachment ---
        let pdfBlob = null;
        try {
          const response = await axiosInstance.get(`/orders/${order.id}/pdf`, {
            responseType: 'blob',
            timeout: 30000
          });
          if (response.data.type !== 'application/pdf') {
            throw new Error('Invalid response format');
          }
          pdfBlob = new Blob([response.data], { type: 'application/pdf' });
          setEmailAttachments([
            {
              filename: `Order_${order.order_number || order.id}.pdf`,
              size: pdfBlob.size,
              content_type: 'application/pdf',
              blob: pdfBlob,
            }
          ]);
        } catch (err) {
          setEmailAttachments([]); // fallback: no attachment
          console.error('Error fetching order PDF for email attachment:', err);
        }

        setNewEmail({
          to: recipientEmail,
          cc: '',
          bcc: '',
          subject: emailSubject,
          content: emailContent,
        });

        setEmailContext({
          type: 'order',
          data: order,
          activities: activities,
          sender: user
        });

        setIsEmailModalOpen(true);
      } catch (error) {
        console.error('Error preparing email data:', error);
        setSnackbar({
          open: true,
          message: 'Error preparing email data. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsEmailButtonLoading(false);
      }
    } else {
      setIsEmailModalOpen(false);
      setEmailAttachments([]); // Clear attachments when closing
    }
  };

  const handleGenerateEmail = async () => {
    setIsGeneratingEmail(true);
    setEmailError('');

    try {
      const context = emailContext;
      const response = await axiosInstance.post('/emails/generate', {
        context: context
      });

      if (response.data) {
        setNewEmail(prev => ({
          ...prev,
          content: response.data.content,
          subject: response.data.subject || prev.subject
        }));
      }
    } catch (error) {
      console.error('Error generating email:', error);
      setEmailError(error.message || 'Failed to generate email content');
    } finally {
      setIsGeneratingEmail(false);
    }
  };

  // In AccountDetail.js, modify the handleSendEmail function:

  const formatTimeForPayload = (date) => {
    return date.toTimeString().split(' ')[0]; // Returns time in HH:MM:SS format
  };

  // Add this helper function near other utility functions
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // First replace <br>, <p>, and other block elements with newlines
    const textWithNewlines = html
      .replace(/<\/(p|div|br|h[1-6])>/gi, '\n')
      .replace(/<br\/?>/gi, '\n');
    // Then remove all HTML tags and decode HTML entities
    const div = document.createElement('div');
    div.innerHTML = textWithNewlines;
    return div.textContent || div.innerText || '';
  };

  // Update the handleSendEmail function
  const handleSendEmail = async () => {
    setIsEmailSending(true);
    try {
      if (!newEmail.to || !newEmail.subject || !newEmail.content) {
        const missingFields = [];
        if (!newEmail.to) missingFields.push('To');
        if (!newEmail.subject) missingFields.push('Subject');
        if (!newEmail.content) missingFields.push('Content');
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const attachments = await serializeEmailAttachments(emailAttachments);

      const emailPayload = {
        to: newEmail.to,
        subject: newEmail.subject,
        content: newEmail.content,
        cc: newEmail.cc || '',
        bcc: newEmail.bcc || '',
        parent_table: 'orders',
        parent_table_id: id,
        attachments: attachments.length > 0 ? attachments : undefined,
      };

      // Send email
      const emailResponse = await axiosInstance.post('/emails/send', emailPayload);

      // After email is sent successfully, create an activity with proper time formatting
      const now = new Date();
      const isoDate = now.toISOString();
      const formattedTime = formatTimeForPayload(now);
      const cleanDescription = stripHtmlTags(newEmail.content);

      const activityPayload = {
        title: `Email: ${newEmail.subject}`,
        type: "Email",
        status: "Completed",
        description: cleanDescription,
        starttime: formattedTime,
        endtime: formattedTime,
        due_date: isoDate,
        parent_table: "orders",
        parent_table_id: id,
        assigned_to: user.id,
        assigned_to_name: user.fullname,
        all_day: false,
        color: "#edc949"
      };

      // Create activity
      const activityResponse = await axiosInstance.post('/activities/', activityPayload);

      if (!activityResponse.data) {
        throw new Error('Failed to create activity record');
      }

      setSnackbar({
        open: true,
        message: 'Email sent and activity created successfully',
        severity: 'success',
      });

      setEmailAttachments([]);
      setIsEmailModalOpen(false);

      // Refresh activities list
      await fetchActivities();

    } catch (error) {
      console.error('Error sending email or creating activity:', error);
      let errorMessage = 'Error sending email';

      if (error.response) {
        const { status, data } = error.response;
        errorMessage = typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail[0]?.msg || 'Unknown error'
            : 'Server error';

        if (status === 404 && data.detail === 'Email settings not found') {
          errorMessage = 'Email settings not configured. Please set up your email provider in settings.';
        } else if (status === 400) {
          errorMessage = 'Invalid request. Please check your input.';
        } else if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        }
      } else if (error.request) {
        errorMessage = 'Failed to connect to the server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsEmailSending(false);
    }
  };


  const renderCustomFields = () => {
    if (!customFields || customFields.length === 0) {
      return null;
    }
    return (
      <CustomFieldsSection
        ref={customFieldsRef}
        objectType="orders"
        objectData={order}
        // isEditing={isEditing}
        onFieldUpdate={(field, value) => {
          handleFieldUpdate(field, value);
        }}
        onError={(field, error) => {
          if (error) {
            handleFieldError(field, error);
          }
        }}
        formErrors={formErrors}
      />
    );
  };

  // Update handleSaveChanges to include custom fields
  const handleSaveChanges = async () => {
    if (Object.keys(formErrors).length > 0) {
      setSnackbar({
        open: true,
        message: 'Please fix all errors before saving.',
        severity: 'error',
      });
      return;
    }

    try {
      // Prepare the payload with custom fields
      const payload = {
        ...order,
        ...updatedFields,
        custom_fields: {}, // Add custom_fields object
        updated_at: new Date().toISOString(),
      };

      // Move custom fields to custom_fields object
      Object.keys(payload).forEach(key => {
        if (key.startsWith('cf_')) {
          payload.custom_fields[key] = payload[key];
          delete payload[key];
        }
      });

      console.log('Formatted payload:', payload);

      const response = await axiosInstance.put(`/orders/${order.id}`, payload);

      // Force a complete refresh of the order data
      const refreshResponse = await axiosInstance.get(`/orders/${order.id}`);
      const orderData = refreshResponse.data;

      // Merge custom fields into main data
      if (orderData.custom_fields) {
        Object.entries(orderData.custom_fields).forEach(([key, value]) => {
          orderData[key] = value;
        });
      }

      setOrder(orderData);
      setUpdatedFields({});
      setFormErrors({});
      setSnackbar({
        open: true,
        message: 'Order updated successfully!',
        severity: 'success',
      });
      // --- Log order update ---
      await frontendLogger.logUpdate('Order', {
        id: order.id,
        updated_fields: Object.keys(updatedFields),
        changes: updatedFields,
        action: 'update',
        user: user.username,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error updating order:', error);
      setSnackbar({
        open: true,
        message: 'Error updating order. Please try again.',
        severity: 'error',
      });
    }
  };

  const fetchInvoiceHtml = async (invoiceId) => {
    try {
      console.log('Fetching HTML invoice...');
      console.log('Invoice ID:', invoiceId);
      const response = await axiosInstance.get(`/invoices/html/${invoiceId}`);
      console.log('Invoice HTML Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice HTML:', error);
      return null;
    }
  };


  // const displayInvoice = async (invoiceId) => {
  //   setIsViewLoading(true);
  //   setViewLoadingId(invoiceId);
  //   try {
  //     const invoiceHtml = await fetchInvoiceHtml(invoiceId);
  //     if (invoiceHtml) {
  //       setCurrentInvoiceHtml(invoiceHtml);
  //       setOpenInvoiceDialog(true);
  //     }
  //   } catch (error) {
  //     console.error('Error viewing invoice:', error);
  //     enqueueSnackbar('Failed to load invoice', { variant: 'error' });
  //   } finally {
  //     setIsViewLoading(false);
  //     setViewLoadingId(null);
  //   }
  // };
  const displayInvoice = async (invoiceId) => {
    try {
      setIsViewLoading(true);
      setViewLoadingId(invoiceId);

      // Fetch full invoice details
      const invoiceResponse = await axiosInstance.get(`/invoices/${invoiceId}/`);

      // Fetch order details if sales_order_id exists
      let orderCurrency = 'INR'; // Default currency
      if (invoiceResponse.data.sales_order_id) {
        try {
          const orderResponse = await axiosInstance.get(`/orders/${invoiceResponse.data.sales_order_id}`);
          if (orderResponse.data.account_id) {
            const accountResponse = await axiosInstance.get(`/accounts/${orderResponse.data.account_id}`);
            orderCurrency = accountResponse.data.currency || 'INR';
          }
        } catch (error) {
          console.error('Error fetching order details:', error);
        }
      }

      // Fetch invoice items
      const invoiceItemsResponse = await axiosInstance.get(`/invoices/${invoiceId}/items/`);

      setViewInvoice({
        ...invoiceResponse.data,
        account_currency: orderCurrency,
        invoice_date: invoiceResponse.data.invoice_date
          ? new Date(invoiceResponse.data.invoice_date).toISOString().split('T')[0]
          : null,
        due_date: invoiceResponse.data.due_date
          ? new Date(invoiceResponse.data.due_date).toISOString().split('T')[0]
          : null,
      });

      setViewInvoiceItems(invoiceItemsResponse.data);
      setOpenViewInvoiceModal(true);

    } catch (error) {
      console.error("Error fetching invoice details:", error);
      enqueueSnackbar("Failed to load invoice details", { variant: "error" });
    } finally {
      setIsViewLoading(false);
      setViewLoadingId(null);
    }
  };



  const handleDownloadInvoice = async (invoiceId) => {
    if (isDownloading) {
      console.warn("Download already in progress");
      return;
    }

    setIsDownloading(true);
    setDownloadLoadingId(invoiceId);

    try {
      // Fetch the templates to check the default template
      const templatesResponse = await axiosInstance.get('/templates/');
      const defaultTemplate = templatesResponse.data.find(template => template.is_default && template.type === 'invoice');
      const isIndiaTemplate = defaultTemplate && defaultTemplate.name === 'Standard India Template';

      const response = await axiosInstance.get(`/invoices/pdf/${invoiceId}`, {
        responseType: 'blob',
        params: { isIndiaTemplate },
        timeout: 30000
      });

      if (response.data.type !== 'application/pdf') {
        throw new Error('Invalid response format');
      }

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      let filename = `Invoice_${invoiceId}.pdf`;

      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.+)/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          filename = fileNameMatch[1].replace(/["']/g, '');
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);

      enqueueSnackbar("Invoice downloaded successfully", { variant: "success" });
    } catch (error) {
      console.error("Error downloading invoice:", error);
      let errorMessage = "Failed to download invoice";

      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timed out. The server is taking too long to respond.";
      } else if (error.response?.data) {
        try {
          if (error.response.data instanceof Blob) {
            const reader = new FileReader();
            const text = await new Promise((resolve) => {
              reader.onload = () => resolve(reader.result);
              reader.readAsText(error.response.data);
            });
            const result = JSON.parse(text);
            errorMessage = result.detail || errorMessage;
          }
        } catch (e) {
          console.error("Error parsing error response:", e);
        }
      }

      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsDownloading(false);
      setDownloadLoadingId(null);
    }
  };
  const InvoiceCard = ({ invoice, onEdit, onView, onDownload }) => {
    const navigate = useNavigate(); // Add this at the top of the component

    // Add click handler
    const handleCardClick = () => {
      navigate(`/invoices/${invoice.id}`);
    };

    const displayInvoiceNumber = invoice.invoice_number || `INV-${String(invoice.id).padStart(4, '0')}`;

    const getInvoiceStatusPillSx = (status) => {
      const normalized = (status || '').toLowerCase();
      if (normalized === 'draft') {
        return { bgcolor: '#F6D4A6', color: '#8A4B00' };
      }
      if (normalized === 'pending') {
        return { bgcolor: '#D6E7FF', color: '#0E4EA8' };
      }
      if (normalized === 'paid' || normalized === 'completed') {
        return { bgcolor: '#DDF3E5', color: '#1F8A56' };
      }
      if (normalized === 'overdue' || normalized === 'cancelled') {
        return { bgcolor: '#FEE2E2', color: '#BA1A1A' };
      }
      return { bgcolor: '#E5E7EB', color: '#374151' };
    };

    // Format currency
    const formatCurrency = (amount, currency = 'INR') => {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency
      }).format(amount || 0);
    };

    // Format date
    const formatInvoiceDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).format(date);
    };

    // Common styles for action icons
    const actionIconStyle = {
      width: 24,
      height: 24,
      backgroundColor: 'rgba(0,0,0,0.04)',
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.08)',
      },
      marginLeft: 0.5,
      position: 'relative'
    };

    return (
      <Box
        onClick={handleCardClick} // Add click handler
        sx={{
          mb: 1,
          transition: 'all 0.2s',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'rgba(0,0,0,0.08)',
          overflow: 'hidden',
          cursor: 'pointer', // Add cursor pointer
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
            borderColor: 'rgba(0,0,0,0.12)',
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: invoice.status ? '1px solid rgba(0,0,0,0.05)' : 'none',
        }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#344767', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
              {displayInvoiceNumber}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight="medium" sx={{ mr: 1.5, color: '#344767' }}>
              {formatCurrency(invoice.total_amount, invoice.currency)}
            </Typography>
            <Chip
              label={invoice.status || 'No Status'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                '.MuiChip-label': { px: 1 },
                ...getInvoiceStatusPillSx(invoice.status),
              }}
            />
          </Box>
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.75,
          bgcolor: 'rgba(0,0,0,0.02)'
        }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
            Updated: {invoice.updated_at ? formatInvoiceDate(invoice.updated_at) : '-'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={invoice.is_gst_invoice_generated ? "Cannot edit invoice after GST e-invoice generation" : "Edit"}>
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click
                    onEdit(invoice.id);
                  }}
                  disabled={isEditInvoiceLoading || invoice.is_gst_invoice_generated}
                  sx={actionIconStyle}
                >
                  {isEditInvoiceLoading && invoice.id === viewLoadingId ? (
                    <CircularProgress size={16} thickness={4} sx={{ color: 'text.secondary' }} />
                  ) : (
                    <Edit sx={{ fontSize: 14, color: 'text.secondary' }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() => onView(invoice.id)}
                disabled={isViewLoading && viewLoadingId === invoice.id}
                sx={actionIconStyle}
              >
                {isViewLoading && viewLoadingId === invoice.id ? (
                  <CircularProgress size={16} thickness={4} sx={{ color: 'text.secondary' }} />
                ) : (
                  <Visibility sx={{ fontSize: 14, color: 'text.secondary' }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={() => onDownload(invoice.id)}
                disabled={isDownloading && downloadLoadingId === invoice.id}
                sx={actionIconStyle}
              >
                {isDownloading && downloadLoadingId === invoice.id ? (
                  <CircularProgress size={16} thickness={4} sx={{ color: 'text.secondary' }} />
                ) : (
                  <Download sx={{ fontSize: 14, color: 'text.secondary' }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  };

  // Update the renderInvoicesSection function
  const renderInvoicesSection = () => (
    <Card sx={{ mt: 0 }}> {/* Remove default top margin */}
      <MDBox
        mx={2}
        mt={-3}
        py={2} // Reduced from py={3}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Receipt sx={{ color: 'white !important', mr: 1 }} />
          <MDTypography variant="h6" color="white">
            Invoices
          </MDTypography>
        </Box>
      </MDBox>

      <MDBox p={1}> {/* Reduced from p={2} */}
        {isInvoicesLoading ? (
          <MDBox display="flex" justifyContent="center" p={1}> {/* Reduced from p={2} */}
            <CircularProgress />
          </MDBox>
        ) : invoices.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 2, // Reduced from py={3}
              px: 1, // Reduced from px={2}
            }}
          >
            <Box sx={{
              width: 30, // Reduced from 50
              height: 30, // Reduced from 50
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.04)',
              mb: 1 // Reduced from mb: 1.5
            }}>
              <Receipt sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              No invoices to display.
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
              Generate your first invoice by clicking the "Create Invoice" button above.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: invoices.length > 2 ? '300px' : 'auto',
              overflowY: invoices.length > 2 ? 'auto' : 'visible',
              px: 0.5, // Reduced from px: 2
              py: 0.5, // Reduced from py: 1.5
              mb: -1, // Reduced from mb: 2
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '3px',
              }
            }}
          >
            {invoices.map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                onEdit={handleEditInvoice}
                onView={displayInvoice}
                onDownload={handleDownloadInvoice}
              />
            ))}
          </Box>
        )}
      </MDBox>
    </Card>
  );


  // Add these helper functions



  // const handleDownloadInvoice = async (invoiceId) => {
  //   try {
  //     const response = await axiosInstance.get(`/invoices/${invoiceId}/download`, {
  //       responseType: 'blob'
  //     });

  //     // Create a link and trigger download
  //     const url = window.URL.createObjectURL(new Blob([response.data]));
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', `invoice_${invoiceId}.pdf`);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //   } catch (error) {
  //     console.error('Error downloading invoice:', error);
  //     setSnackbar({
  //       open: true,
  //       message: 'Failed to download invoice',
  //       severity: 'error'
  //     });
  //   }
  // };

  const renderStepper = () => {
    const isInFinalStage = order?.status === 'Closed' || order?.status === 'Cancelled';

    return (
      <MDBox>
        {/* <MDTypography variant="button" color="white" mb={2}>
          Status: {order?.status}
          {isInFinalStage && (
            <Chip
              label="Final Stage"
              color="primary"
              size="small"
              sx={{ ml: 2, mb: 0.5 }}
            />
          )}
        </MDTypography> */}

        <ThemedCustomStepper
          activeStep={activeStep}
          steps={orderStages}
          onStepClick={!isInFinalStage ? handleStageChange : undefined}
          disabled={isInFinalStage}
          completedSteps={orderStages
            .slice(0, activeStep + 1)
            .map((_, index) => index)}
          stepProps={orderStages.map((stage, index) => ({
            completed: index <= activeStep,
            disabled:
              isInFinalStage && index !== activeStep,
            sx: {
              '& .MuiStepLabel-label': {
                color:
                  isInFinalStage && index === activeStep
                    ? 'primary.main'
                    : 'inherit',
                fontWeight:
                  isInFinalStage && index === activeStep ? 'bold' : 'inherit',
              },
            },
          }))}
        />
      </MDBox>
    );
  };

  // Handle stage change
  // const handleStageChange = async (index) => {
  //   const targetStage = orderStages[index];

  //   if (isLoading) return;

  //   setIsLoading(true);
  //   try {
  //     const payload = {
  //       status: targetStage,
  //       // Add any additional fields you want to update
  //     };

  //     const response = await axiosInstance.put(`/orders/${order.id}`, payload);

  //     // Update order state
  //     setOrder(response.data);

  //     // Update active step
  //     setActiveStep(index);

  //     setSnackbar({
  //       open: true,
  //       message: `Order status updated to ${targetStage}`,
  //       severity: 'success',
  //     });
  //   } catch (error) {
  //     console.error('Error updating order status:', error);
  //     setSnackbar({
  //       open: true,
  //       message: 'Error updating order status',
  //       severity: 'error',
  //     });
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleStageChange = (index) => {
    const targetStage = orderStages[index];
    if (isLoading || targetStage === order?.status) return;
    setOrderStatusConfirm({ open: true, index });
  };

  const handleOrderStatusConfirmCancel = () => {
    if (isDetailsLoading) return;
    setOrderStatusConfirm({ open: false, index: null });
  };

  const applyOrderStageChange = async (index) => {
    const targetStage = orderStages[index];

    setIsDetailsLoading(true);
    try {
      const payload = {
        status: targetStage,
      };

      console.log('Starting order status update process...', {
        order_id: order.id,
        current_status: order.status,
        target_status: targetStage
      });

      const response = await axiosInstance.put(`/orders/${order.id}`, payload);

      // If the order is being cancelled, update the inventory
      if (targetStage === 'Cancelled') {
        console.log('Order cancelled - starting inventory restoration process...');

        for (const item of order.order_items) {
          try {
            console.log('Processing item for inventory restoration:', {
              product_name: item.product_name,
              quantity_to_restore: item.quantity
            });

            // Get current inventory state
            const inventoryResponse = await axiosInstance.get(`/inventory/?product_id=${item.product_id}`);
            console.log(`Raw inventory response for product ${item.product_id}:`, inventoryResponse.data);

            const inventoryItem = inventoryResponse.data.find(inv => inv.product_id === item.product_id);

            if (!inventoryItem) {
              throw new Error(`Inventory not found for product ${item.product_name}`);
            }

            const currentQuantity = inventoryItem.quantity;
            const quantityToRestore = item.quantity;
            const newQuantity = currentQuantity + quantityToRestore;

            console.log('Processing inventory restoration:', {
              inventory_id: inventoryItem.id,
              product_id: item.product_id,
              product_name: item.product_name,
              current_quantity: currentQuantity,
              quantity_to_restore: quantityToRestore,
              calculated_new_quantity: newQuantity
            });

            // Update inventory using PUT
            const updateResponse = await axiosInstance.put(`/inventory/${inventoryItem.id}`, {
              ...inventoryItem,
              quantity: newQuantity,
              // updated_by: user?.id,
              // updated_by_name: user?.username
            });

            // Verify the update
            const verifyResponse = await axiosInstance.get(`/inventory/?product_id=${item.product_id}`);
            const updatedInventory = verifyResponse.data.find(inv => inv.product_id === item.product_id);

            console.log('Inventory restoration verification:', {
              product_id: item.product_id,
              product_name: item.product_name,
              original_quantity: currentQuantity,
              restored_quantity: quantityToRestore,
              expected_new_quantity: newQuantity,
              actual_new_quantity: updatedInventory.quantity
            });

            if (updatedInventory.quantity !== newQuantity) {
              throw new Error(
                `Inventory restoration failed for ${item.product_name}. ` +
                `Expected: ${newQuantity}, Got: ${updatedInventory.quantity}`
              );
            }

            console.log(`Successfully restored inventory for product ${item.product_name}`);
          } catch (itemError) {
            console.error(`Error processing inventory for item ${item.product_name}:`, itemError);
            throw itemError;
          }
        }
      }

      // Update order state
      setOrder(response.data);
      setActiveStep(index);

      console.log('Order status update completed successfully', {
        order_id: order.id,
        new_status: targetStage,
        step_index: index
      });

      setSnackbar({
        open: true,
        message: `Order status updated to ${targetStage}`,
        severity: 'success',
      });
      // --- Log stage change ---
      await frontendLogger.logUpdate('Order', {
        id: order.id,
        action: 'stage_change',
        old_status: order.status,
        new_status: targetStage,
        user: user.username,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error in applyOrderStageChange:', error);
      setSnackbar({
        open: true,
        message: `Error updating order status: ${error.message}`,
        severity: 'error',
      });
      throw error;
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const handleOrderStatusConfirm = async () => {
    const index = orderStatusConfirm.index;
    if (index == null) {
      handleOrderStatusConfirmCancel();
      return;
    }
    try {
      await applyOrderStageChange(index);
      setOrderStatusConfirm({ open: false, index: null });
    } catch {
      // applyOrderStageChange handles snackbar; keep dialog open on failure
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };



  const renderDetailCard = (title, data, renderContent = null) => (
    <MDBox
      m={3}
      p={3}
      borderRadius="lg"
      sx={{
        backgroundColor: 'white',
        boxShadow: 3,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" fontWeight="medium" color="dark">
          {title}
        </MDTypography>
      </MDBox>

      <Grid container spacing={3}>
        {Array.isArray(data) && data.length > 0 ? (
          data.map((item) => (
            <Grid item xs={12} sm={6} key={item.label || item.field}>
              <MDBox display="flex" alignItems="center">
                <MDTypography variant="button" fontWeight="bold" color="text">
                  {item.label}:
                </MDTypography>
                <MDTypography color="text" ml={1}>
                  {item.type === 'currency'
                    ? formatCurrencyByAccount(item.value || order?.[item.field], getOrderDisplayCurrency())
                    : (() => {
                      const value = item.value || (item.field && order?.[item.field]) || '';
                      // Capitalize first letter for string values, but skip currency and email fields
                      if (typeof value === 'string' && value && !item.field?.includes('email') && !item.field?.includes('currency')) {
                        return capitalizeFirstLetter(value);
                      }
                      return value;
                    })()}
                </MDTypography>
              </MDBox>
            </Grid>
          ))
        ) : (
          !Array.isArray(data) &&
          Object.entries(data).map(([key, value]) => (
            <Grid item xs={12} sm={6} key={key}>
              <MDBox display="flex" alignItems="center">
                <MDTypography variant="button" fontWeight="bold" color="text">
                  {key}:
                </MDTypography>
                <MDTypography fontSize="0.9rem" color="text" ml={1}>
                  {typeof value === 'object' && value !== null ? value :
                    (() => {
                      // Handle currency fields
                      if (key.toLowerCase().includes('amount') || key.toLowerCase().includes('total') || key.toLowerCase().includes('subtotal')) {
                        return formatCurrencyByAccount(parseFloat(value) || 0, getOrderDisplayCurrency());
                      }
                      // Capitalize first letter for string values, but skip email and currency fields
                      if (typeof value === 'string' && value && !key.toLowerCase().includes('email') && !key.toLowerCase().includes('currency')) {
                        return capitalizeFirstLetter(value);
                      }
                      return value;
                    })()}
                </MDTypography>
              </MDBox>
            </Grid>
          ))
        )}
      </Grid>
      {renderContent && (
        <MDBox mt={3}>
          {typeof renderContent === 'function' ? renderContent() : renderContent}
        </MDBox>
      )}
    </MDBox>
  );

  const renderOrderItemsTable = () => (
    <MDBox
      m={3}
      p={3}
      borderRadius="lg"
      sx={{
        backgroundColor: 'white',
        boxShadow: 3,
        maxWidth: '100%',
        width: 'auto'
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h6" fontWeight="medium" color="dark">
          Order Items
        </MDTypography>
      </MDBox>

      <MDBox
        sx={{
          mt: 3,
          mb: 3,
          border: "1px solid #dee2e6",
          borderRadius: "0.75rem",
          overflow: "auto",
          maxHeight: "400px",
        }}
      >
        {/* Shared column tracks keep headers and values aligned */}
        {(() => {
          const itemsGridSx = {
            display: "grid",
            gridTemplateColumns:
              "minmax(180px, 2.2fr) minmax(110px, 1fr) minmax(72px, 0.7fr) minmax(100px, 0.85fr) minmax(130px, 1.1fr) minmax(90px, 0.75fr) minmax(130px, 1.1fr)",
            alignItems: "center",
            width: "100%",
            minWidth: 920,
          };
          const headerCellSx = {
            px: 1.5,
            py: 1.5,
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.35,
          };
          // PSA-373: line-item cells wrap when the column is narrow.
          const valueCellSx = {
            px: 1.5,
            py: 1.5,
            textAlign: "right",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.35,
          };

          return (
            <>
              {/* Header */}
              <MDBox
                sx={{
                  ...itemsGridSx,
                  backgroundColor: "#f8f9fa",
                  borderBottom: "1px solid #dee2e6",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <MDBox sx={{ ...headerCellSx, textAlign: "left" }}>
                  <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                    Product Name
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                  <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                    Unit Price
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                  <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                    Tax %
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                  <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                    Discount %
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                  <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                    Discounted Price
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                  <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                    Quantity
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ ...headerCellSx, textAlign: "right" }}>
                  <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                    Total Net Price
                  </MDTypography>
                </MDBox>
              </MDBox>

              {/* Body */}
              {order.order_items.map((item, index) => (
                <MDBox
                  key={index}
                  sx={{
                    ...itemsGridSx,
                    borderBottom: "1px solid #dee2e6",
                    "&:hover": { backgroundColor: "#f8f9fa" },
                    transition: "background-color 0.2s",
                  }}
                >
                  <MDBox sx={{ ...headerCellSx, textAlign: "left" }}>
                    <MDTypography
                      variant="button"
                      color="text"
                      sx={{
                        display: "block",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        lineHeight: 1.35,
                      }}
                      title={item.product_name}
                    >
                      {item.product_name}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={valueCellSx}>
                    <MDTypography variant="button" color="text">
                      {formatCurrencyByAccount(parseFloat(item.unit_price), getOrderDisplayCurrency())}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={valueCellSx}>
                    <MDTypography variant="button" color="text">
                      {item.tax}%
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={valueCellSx}>
                    <MDTypography variant="button" color="text">
                      {item.discount_percentage}%
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={valueCellSx}>
                    <MDTypography variant="button" color="text">
                      {formatCurrencyByAccount(parseFloat(item.discounted_price), getOrderDisplayCurrency())}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={valueCellSx}>
                    <MDTypography variant="button" color="text">
                      {item.quantity}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={valueCellSx}>
                    <MDTypography variant="button" color="text">
                      {formatCurrencyByAccount(
                        (parseFloat(item.discounted_price) || 0) * (parseFloat(item.quantity) || 0),
                        getOrderDisplayCurrency()
                      )}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              ))}

              {/* Footer */}
              <MDBox
                sx={{
                  ...itemsGridSx,
                  borderTop: "2px solid #dee2e6",
                  backgroundColor: "white",
                }}
              >
                <MDBox sx={{ ...valueCellSx, gridColumn: "1 / 7" }}>
                  <MDTypography variant="button" fontWeight="medium" color="dark">
                    Total
                  </MDTypography>
                </MDBox>
                <MDBox sx={valueCellSx}>
                  <MDTypography variant="button" fontWeight="medium" color="dark">
                    {formatCurrencyByAccount(parseFloat(order.total_amount), getOrderDisplayCurrency())}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </>
          );
        })()}
      </MDBox>
    </MDBox>
  );

  const renderRelatedDocuments = (title, data, icon) => {
    if (!data) return null;

    return (
      <Card sx={{ mb: 3, overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
        <MDBox p={3}>
          <Grid container spacing={2} alignItems="center" mb={2}>
            <Grid item>{icon}</Grid>
            <Grid item>
              <MDTypography variant="h5" fontWeight="medium">
                {title}
              </MDTypography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {Object.entries(data).map(([key, value]) => (
              <Grid item xs={12} key={key}>
                <MDTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                  {key}
                </MDTypography>
                <MDTypography variant="body2" fontWeight="regular" color="dark">
                  {value}
                </MDTypography>
                <Divider sx={{ my: 1 }} />
              </Grid>
            ))}
          </Grid>
          <MDBox mt={3} mb={1} textAlign="center">
            <MDButton variant="outlined" color="info" startIcon={<Download />}>
              Upload {title}
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" height="80vh">
          <CircularProgress />
        </MDBox>
        
      </DashboardLayout>
    );
  }

  // Check if the user has permission to access this lead
  if (isUnauthorized) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <UnauthorizedAccess />
        
      </DashboardLayout>
    );
  }

  const headers = [
    { id: 'name', label: 'Name' },
    { id: 'email', label: 'Email' },
    { id: 'role', label: 'Role' },

    { id: 'lastActive', label: 'Last Active' },
  ];

  const rows = [
    { name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'active', lastActive: '2023-09-15' },
    { name: 'Jane Smith', email: 'jane@example.com', role: 'User', status: 'inactive', lastActive: '2023-08-30' },
    // Add more rows as needed
  ];

  // Add this function after fetchOrderData
  const updateInventoryForCompletedOrder = async (orderItems) => {
    try {
      for (const item of orderItems) {
        // Get the product ID for the item
        const productResponse = await axiosInstance.get('/products/', {
          params: { name: item.product_name }
        });

        if (productResponse.data?.length > 0) {
          const product = productResponse.data[0];
          const productId = product.id;

          // Only update inventory for physical products
          if (product.product_type === 'Physical') {
            // Decrease inventory by the ordered quantity (negative quantity_change)
            await axiosInstance.post(`/inventory/adjust/${productId}`, {
              quantity_change: -item.quantity // Negative to decrease inventory
            });

            console.log(`Updated inventory for physical product ${item.product_name}`);
          } else if (product.product_type === 'Service' || product.product_type === 'Digital') {
            // For non-physical products, ensure quantity is 1
            if (item.quantity !== 1) {
              // Update order item with quantity = 1
              await axiosInstance.put(`/orders/items/${item.id}`, {
                ...item,
                quantity: 1
              });
              console.log(`Fixed quantity to 1 for ${product.product_type} product: ${item.product_name}`);
            } else {
              console.log(`Skipping inventory update for ${product.product_type} product: ${item.product_name}`);
            }
          } else {
            // Default to physical if product_type is not specified
            await axiosInstance.post(`/inventory/adjust/${productId}`, {
              quantity_change: -item.quantity
            });
            console.log(`Updated inventory for product ${item.product_name} (assumed physical)`);
          }
        } else {
          console.warn(`Product not found: ${item.product_name}`);
        }
      }
    } catch (error) {
      console.error('Error updating inventory:', error);
      throw error;
    }
  };

  // Modify the existing handleStatusChange function
  const handleStatusChange = async (newStatus) => {
    try {
      if (newStatus === 'Completed' && order.status !== 'Completed') {
        console.log('Order marked as completed, updating inventory...'); // Debug log

        // Get the latest order items
        const itemsResponse = await axiosInstance.get(`/orders/items/${order.id}`);
        const currentOrderItems = itemsResponse.data;

        // Update inventory
        await updateInventoryForCompletedOrder(currentOrderItems);
      }

      // Update order status
      const updatedOrder = await axiosInstance.put(`/orders/${order.id}`, {
        ...order,
        status: newStatus
      });

      setOrder(updatedOrder.data);
      setSnackbar({
        open: true,
        message: 'Order status updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to update order status',
        severity: 'error'
      });
    }
  };

  function formatOrderDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  // If there's a specific place where the currency code alone is displayed,
  // you might need to update that to show the full currency name.
  // For example, in a display or form field:
  // const renderCurrencyInfo = (currencyCode) => {
  //   const currencyObj = currencies.find(c => c.code === currencyCode) ||
  //     currencies.find(c => c.code === 'INR');
  //   return `${currencyObj?.name || 'Indian Rupee'} ${currencyObj?.code || 'INR'}`;
  // };

  // Define a mapping for transport modes
  const transportModeMapping = {
    '1': 'Road',
    '2': 'Rail',
    '3': 'Air',
    '4': 'Ship',
  };

  const vehicleTypeMapping = {
    'R': 'Regular',
    'C': 'Container',
    'O': 'Over Dimensional Cargo',
    // Add other mappings as needed
  };

  const handleOrderPdfDownload = async () => {
    if (isOrderPdfDownloading) return;
    setIsOrderPdfDownloading(true);
    setOrderPdfDownloadId(order.id);

    try {
      const response = await axiosInstance.get(`/orders/${order.id}/pdf`, {
        responseType: 'blob',
        timeout: 30000
      });

      if (response.data.type !== 'application/pdf') {
        throw new Error('Invalid response format');
      }

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      let filename = `Order_${order.order_number || order.id}.pdf`;

      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.+)/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          filename = fileNameMatch[1].replace(/["']/g, '');
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);

      enqueueSnackbar('Order PDF downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading order PDF:', error);
      enqueueSnackbar(error.message || 'Failed to download order PDF', { variant: 'error' });
    } finally {
      setIsOrderPdfDownloading(false);
      setOrderPdfDownloadId(null);
    }
  };

  // Echo/Feed functionality functions
  // const fetchFeedItems = async () => {
  //   if (!id) return;
  //   setIsFeedLoading(true);
  //   try {
  //     const response = await axiosInstance.get(`/feed/orders/${id}`);
  //     setFeedItems(response.data || []);
  //   } catch (error) {
  //     console.error('Error fetching feed items:', error);
  //     setFeedItems([]);
  //   } finally {
  //     setIsFeedLoading(false);
  //   }
  // };

  // Shared by the classic and v2 layouts below: the same actions, field
  // sections and side column render in both.
  const recordActions = (
    <>

                        <Tooltip title="Send Email" placement="top">
                          <IconButton
                            onClick={handleToggleEmailModal}
                            disabled={isEmailButtonLoading}
                            sx={{
                              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                              borderRadius: '50%',
                              padding: '8px',
                              '&:hover': {
                                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            {isEmailButtonLoading ? (
                              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                            ) : (
                              <EmailIcon sx={{ color: '#ffffff', fontSize: 20 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        {/* Edit Button - Only show if order is not Cancelled or Completed */}
                        {order.status !== 'Cancelled' && order.status !== 'Completed' && (
                          <Tooltip title="Edit Order" placement="top">
                            <IconButton
                              onClick={() => setIsEditModalOpen(true)}
                              sx={{
                                background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                                borderRadius: '50%',
                                padding: '8px',
                                '&:hover': {
                                  background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <Edit sx={{ color: '#ffffff', fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Invoice Button - Only show if order is In Progress or Completed */}
                        {/* {(order.status === 'In Progress' || order.status === 'Completed') && ( */}
                        <Tooltip title="Create Invoice" placement="top">
                          <IconButton
                            onClick={handleInvoiceClick}
                            disabled={isInvoiceButtonLoading}
                            sx={{
                              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                              borderRadius: '50%',
                              padding: '8px',
                              '&:hover': {
                                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            {isInvoiceButtonLoading ? (
                              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                            ) : (
                              <Description sx={{ color: '#ffffff', fontSize: 20 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        {/* )} */}

                        {/* Clone Button */}
                        <Tooltip title="Clone Order" placement="top">
                          <IconButton
                            onClick={() => setIsCloneModalOpen(true)}
                            sx={{
                              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                              borderRadius: '50%',
                              padding: '8px',
                              '&:hover': {
                                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            <FileCopy sx={{ color: '#ffffff', fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download PDF" placement="top">
                          <IconButton
                            onClick={handleOrderPdfDownload}
                            disabled={isOrderPdfDownloading}
                            sx={{
                              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                              borderRadius: '50%',
                              padding: '8px',
                              '&:hover': {
                                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            {isOrderPdfDownloading ? (
                              <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                            ) : (
                              <PictureAsPdf sx={{ color: '#ffffff', fontSize: 20 }} />
                            )}
                          </IconButton>
                        </Tooltip>

                        {/* Delete Button - Commented out as in original code */}
                        {/* {!isEditing && (
        <Tooltip title="Delete Order" placement="top">
          <IconButton
            aria-label="delete"
            onClick={handleDelete}
            sx={{
              background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
              borderRadius: '50%',
              padding: '8px',
              '&:hover': {
                background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Delete sx={{ color: '#ffffff', fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )} */}
    </>
  );
  const recordDetailsBody = (
    <>
      {isDetailsLoading ? (
        <OrderDetailsSkeleton />
      ) : (
        <>
          {visibleSections.map(sectionLabel => {
            if (sectionLabel === 'Order Information') {
              return renderDetailCard("Order Information", {
                "Order Number": order.order_number,
                "PO Number": order.po_number,
                "Order Creation Date": formatOrderDate(order.created_at),
                "Status": order.status,
                "Subtotal": order.subtotal || 0,
                "Total Amount": order.total_amount || 0,
                "Currency": order.currency,
                "Account": (
                  <MDTypography
                    component="span"
                    onClick={() => navigate(`/accounts/${order.account_id}`)}
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {capitalizeFirstLetter(order.account_name || ' ')}
                  </MDTypography>
                ),
                "Contact": (
                  <MDTypography
                    component="span"
                    onClick={() => navigate(`/contacts/${order.contact_id}`)}
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {capitalizeFirstLetter(order.contact_name || ' ')}
                  </MDTypography>
                ),
                "Deal": (
                  <MDTypography
                    component="span"
                    onClick={handleDealClick}
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {capitalizeFirstLetter(order.deal_name)}
                  </MDTypography>
                ),
              });
            }
            if (sectionLabel === 'Shipping & Payment') {
              return renderDetailCard("Shipping & Payment", {
                "Billing Address": (order.billing_address && order.billing_address !== 'None') ? order.billing_address : '',
               "Shipping Address": (order.shipping_address && order.shipping_address !== 'None') ? order.shipping_address : '',
                "Shipping Method": order.shipping_method || '-',
                "Delivery Date": formatOrderDate(order.delivery_date),
                "Notes": order.shipping_instruction || '-',
              });
            }
            if (sectionLabel === 'Transport Details') {
              return renderDetailCard("Transport Details", {
                "Transporter ID": order.transid || '-',
                "Transporter Name": order.transname || '-',
                "Transport Mode": transportModeMapping[order.transmode] || order.transmode,
                ...(order.transmode === '1' && {
                  "Vehicle Number": order.vehno || '-',
                  "Vehicle Type": vehicleTypeMapping[order.vehtype] || order.vehtype || '-',
                }),
                ...(order.transmode === '4' && {
                  "Vehicle Number": order.vehno || '-',
                  "Transport Document Number": order.transdocno || '-',
                }),
                ...(['2', '3'].includes(order.transmode) && {
                  "Transport Document Number": order.transdocno || '-',
                }),
              });
            }
            if (sectionLabel === 'Custom Field Information' && customFields && customFields.length > 0) {
              return renderDetailCard("Custom Field Information", [], renderCustomFields());
            }
            if (sectionLabel === 'Order Items') {
              return renderOrderItemsTable();
            }
            if (sectionLabel === 'Audit Information') {
              return (
                <MDBox key="Audit Information">
                  <Card
                    sx={{
                      padding: '30px',
                      boxShadow: 3,
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      margin: '20px',
                    }}
                  >
                    <MDTypography variant="h6" fontWeight="medium">
                      Audit Information
                    </MDTypography>
                    <MDBox pt={3}>
                      <MDBox>
                        <Grid container spacing={3}>
                          {auditFields.map((item) => (
                            <Grid item xs={12} md={6} key={item.field}>
                              <MDBox display="flex" alignItems="center">
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                  color="text"
                                  sx={{ marginRight: '4px', flexShrink: 0 }}
                                >
                                  {item.label}:
                                </MDTypography>
                                {item.userIdField ? (
                                  <Link to={`/users/${order[item.userIdField]}/profile`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                    <MDTypography color="info" sx={{ fontSize: '0.9rem', fontWeight: 'medium' }}>
                                      {(() => {
                                        const value = order[item.field] || ' ';
                                        if (typeof value === 'string' && value && !item.field?.includes('email')) {
                                          return capitalizeFirstLetter(value);
                                        }
                                        return value;
                                      })()}
                                    </MDTypography>
                                  </Link>
                                ) : (
                                  <MDTypography color="text" sx={{ fontSize: '0.9rem', fontWeight: 'regular' }}>
                                    {(() => {
                                      const value = item.field === 'created_at' || item.field === 'updated_at'
                                        ? formatDate(order[item.field])
                                        : order[item.field] || ' ';
                                      if (typeof value === 'string' && value &&
                                        !item.field?.includes('email') &&
                                        item.field !== 'created_at' &&
                                        item.field !== 'updated_at') {
                                        return capitalizeFirstLetter(value);
                                      }
                                      return value;
                                    })()}
                                  </MDTypography>
                                )}
                              </MDBox>
                            </Grid>
                          ))}
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              );
            }
            return null;
          })}
          <Divider sx={{ my: 3 }} />
        </>
      )}
    </>
  );
  // Side-column pieces, named so the v2 record layout can place them in its
  // tabs and side column; recordSideColumn below keeps the classic order.
  const orderActivitiesCard = (
    <>
      {hasTasksModule && (
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Activities
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={handleOpenActivityModal}>
            Add Activity
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          {isActivitiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} color="info" />
            </Box>
          ) : (
            <Activities
              activities={activities.filter(Boolean)}
              onDelete={handleDeleteActivity}
              onEdit={handleEditActivity}
            />
          )}
        </MDBox>
      </Card>
      )}
    </>
  );

  // Attachments.
  const orderAttachmentsCard = (
    <>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachmentIcon sx={{ color: 'white !important', mr: 1 }} />
            <MDTypography variant="h6" color="white">
              Attachments
            </MDTypography>
          </Box>
          <MDButton variant="gradient" color="dark" onClick={() => setIsAttachmentModalOpen(true)}>
            Add Attachment
          </MDButton>
        </MDBox>
        <MDBox p={2}>
          <Attachments
            attachments={attachments}
            onDelete={handleDeleteAttachment}
            onDownload={handleDownloadAttachment}
          />
        </MDBox>
      </Card>
    </>
  );

  // Echo feed.
  const orderEchoFeedCard = (
    <>
      <Card style={{ marginTop: '10px' }}>
        <MDBox
          mx={2}
          mt={-2}
          py={2}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RecordVoiceOverIcon sx={{ color: 'white !important', mr: 1 }} />

            <MDTypography variant="h6" color="white">
              Echo Feed
            </MDTypography>
          </Box>
          <MDButton 
            variant="gradient" 
            color="dark"
            onClick={handleOpenEchoModal}
          >
            Add Message
          </MDButton>
        </MDBox>

        <MDBox p={2}>
          {/* EchoComposer removed, replaced by modal */}
          <Modal
            open={isEchoModalOpen}
            onClose={handleCloseEchoModal}
            aria-labelledby="echo-modal-title"
            aria-describedby="echo-modal-description"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'background.paper',
              boxShadow: 24,
              borderRadius: 2,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <MDTypography id="echo-modal-title" variant="h6" mb={2}>
                Post a Feed
              </MDTypography>
              <EchoComposer
                placeholder="Type @ to mention users..."
                axiosInstance={axiosInstance}
                objectType="orders"
                objectId={id}
                onSubmit={handleEchoSubmit}
                onCancel={handleCloseEchoModal}
                onSuccess={(message) => {
                  setSnackbar({
                    open: true,
                    message: 'Feed item created successfully',
                    severity: 'success',
                  });
                  handleCloseEchoModal();
                  fetchFeedItems(); // Refresh the feed items
                }}
                onError={(message) => setSnackbar({
                  open: true,
                  message: `Error creating feed item: ${message}`,
                  severity: 'error',
                })}
              />
            </Box>
          </Modal>
          {/* Display feed items */}
          <Box mt={3}>
            {isFeedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} color="info" />
              </Box>
            ) : feedItems.length > 0 ? (
              <Box className="pearl-echo-feed" sx={{ 
                maxHeight: 400, 
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa'
              }}>
                {feedItems.map((item, index) => (
                  <Box 
                    key={item.id} 
                    sx={{ 
                      p: 2, 
                      borderBottom: index < feedItems.length - 1 ? '1px solid #e0e0e0' : 'none',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <MDUserAvatar
                        firstName={extractNameParts(item.created_by_user, item.created_by_name).firstName}
                        middleName={extractNameParts(item.created_by_user, item.created_by_name).middleName}
                        lastName={extractNameParts(item.created_by_user, item.created_by_name).lastName}
                        avatarUrl={extractNameParts(item.created_by_user, item.created_by_name).avatarUrl}
                        size={36}
                        variant="circular"
                        sx={{ mr: 2, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, ml:2  }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="subtitle2" 
                              fontWeight="medium"
                              sx={{ 
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              onClick={() => {
                                // Navigate to user profile
                                const userMention = item.feeditem.mentions?.find(m => m.type === 'user' && m.entity_name === item.created_by_name);
                                if (userMention) {
                                  navigate(`/users/${userMention.entity_id}/profile`);
                                }
                              }}
                            >
                              {item.created_by_name}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ ml: 1 }}
                            >
                              {formatDate(item.updated_at, 'datetime')}
                            </Typography>
                          </Box>

                          {/* Edit/Delete buttons - only show for current user's messages */}
                          {item.created_by === user?.id && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Edit message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="info"
                                  onClick={() => startEditFeedItem(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <EditIcon fontSize="small" />
                                </MDButton>
                              </Tooltip>
                              <Tooltip title="Delete message" arrow>
                                <MDButton
                                  size="small"
                                  variant="text"
                                  color="error"
                                  onClick={() => handleDeleteConfirm(item)}
                                  disabled={isEditingFeed}
                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </MDButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {editingFeedItem === item.id ? (
                          <Box sx={{ mt: 1 }}>
                            <EchoComposer
                              value={editFeedContent}
                              onChange={setEditFeedContent}
                              placeholder="Edit your message..."
                              axiosInstance={axiosInstance}
                              objectType="orders"
                              objectId={id}
                              feedItemId={item.id}
                              isEditMode={true}
                              onSubmit={handleEditFeedItem}
                              onCancel={cancelEditFeedItem}
                              onSuccess={() => {
                                // Auto-save when user clicks the Update button in EchoComposer
                                setEditingFeedItem(null);
                                setEditFeedContent('');
                              }}
                              sx={{ 
                                mb: 1,
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'white'
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.5,
                              color: 'text.primary'
                            }}
                          >
                            {parseFeedContent(item)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ 
               display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: -2,
              }}>
                <Box sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.04)',
                    mb: 1.5 }}>
                    <RecordVoiceOverIcon sx={{ color: 'text.secondary', fontSize: 24,}} />
                  </Box>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  No messages yet
                </Typography>
                 {/* <Typography variant="caption">
                  Start a conversation by typing a message above
                </Typography> */}
              </Box>
            )}
          </Box>
        </MDBox>
      </Card>
    </>
  );

  // Upload dialog for Attachments.
  const orderAttachmentUploadModal = (
    <>
      <AttachmentUploadModal
        isOpen={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
        onUpload={handleUploadSuccess}
        axiosInstance={axiosInstance}
        // allowedFileTypes="*/*"
        maxSizeInMB={5}
      />
    </>
  );

  const recordSideColumn = (
    <>
      {orderActivitiesCard}

      {hasTasksModule && (
        <>
          <br />
          <br />
        </>
      )}
      {orderAttachmentsCard}
      <br /><br />
           {/* Echo/Feed Card */}
      {orderEchoFeedCard}
      <br />
      <br />

      {renderInvoicesSection()}
      {orderAttachmentUploadModal}
      <br/>
    </>
  );

  // v2: the order laid out like Account 360 (components/v2/record). The
  // pieces are the ones the classic page renders; order items stay in the
  // field sections on Overview.
  const lastOrderActivity = activities.map((a) => a?.created_at).filter(Boolean).sort().pop();
  const v2Main = (
    <MDBox>
      <RecordPageV2
        sectionsLabel="Order sections"
        hero={{
          label: 'Order summary',
          icon: Package,
          eyebrow: 'Order',
          title: order?.order_number || '',
          animate: isCloning,
          actions: recordActions,
          badges: order?.status ? <StatusBadge status={order.status} /> : null,
          meta: [
            { key: 'account', icon: Building2, content: order?.account_name && order?.account_id ? (
              <Link to={`/accounts/${order.account_id}`} style={{ color: 'inherit', fontWeight: 600 }}>{order.account_name}</Link>
            ) : order?.account_name || null },
            { key: 'salesperson', icon: UserRound, content: order?.salesperson_name ? <>Salesperson <UserLink userId={order.salesperson_id} name={order.salesperson_name} /></> : null },
            { key: 'po', icon: Hash, content: order?.po_number ? `PO ${order.po_number}` : null },
            { key: 'shipping', icon: Truck, content: order?.shipping_method || null },
          ],
        }}
        tiles={[
          { key: 'total', icon: CircleDollarSign, label: 'Total', value: order?.total_amount != null && order?.total_amount !== '' ? formatCurrencyByAccount(order.total_amount, order.currency) : '—' },
          order?.invoiced_amount != null ? { key: 'invoiced', icon: ReceiptIcon, label: 'Invoiced', value: formatCurrencyByAccount(order.invoiced_amount, order.currency), note: `${invoices.length} invoice${invoices.length === 1 ? '' : 's'}`, tab: 'invoices' } : { key: 'invoices', icon: ReceiptIcon, label: 'Invoices', value: String(invoices.length), tab: 'invoices' },
          order?.remaining_amount != null ? { key: 'remaining', icon: Wallet, label: 'Still to invoice', value: formatCurrencyByAccount(order.remaining_amount, order.currency) } : null,
          { key: 'items', icon: ListOrdered, label: 'Items', value: String(orderItems.length) },
          { key: 'last', icon: Clock, label: 'Last activity', value: relativeTime(lastOrderActivity) || 'None yet', tab: 'activity' },
          { key: 'created', icon: CalendarDays, label: 'Created', value: order?.created_at ? formatDate(order.created_at, 'date') : '—' },
        ]}
        tabs={[
          {
            key: 'overview',
            label: 'Overview',
            icon: LayoutGrid,
            render: () => (
              <TabStack>
                <Card sx={{ p: 2.5 }}>{renderStepper()}</Card>
                <Card>
                  <MDBox p={3}>
                    {recordDetailsBody}
                  </MDBox>
                </Card>
              </TabStack>
            ),
          },
          { key: 'invoices', label: 'Invoices', icon: ReceiptIcon, count: invoices.length, render: () => renderInvoicesSection() },
          {
            key: 'activity',
            label: 'Activity',
            icon: History,
            count: hasTasksModule ? activities.filter(Boolean).length : undefined,
            render: () => (
              <TabStack>
                {orderActivitiesCard}
                {orderEchoFeedCard}
              </TabStack>
            ),
          },
          { key: 'files', label: 'Files', icon: Paperclip, count: attachments.length, render: () => orderAttachmentsCard },
        ]}
        aside={(selectTab) => (
          <ActivityTimeline activities={activities} feedItems={feedItems} onOpenAll={() => selectTab('activity')} emptyText="No activity on this order yet." />
        )}
      />
      {orderAttachmentUploadModal}
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* <Grid item xs={12} md={8}>

            <OrderHeader orderNumber={order.order_number} status={order.status}
              extraActions={
                <Tooltip title="Edit Order">
                  <IconButton
                    onClick={() => setIsEditModalOpen(true)}
                    sx={{
                      backgroundColor: "#f0f0f0",
                      borderRadius: "50%",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                      transition: "background-color 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#0275d8",
                        "& .MuiSvgIcon-root": {
                          color: "#f0f0f0",
                        },
                      },
                    }}
                  >
                    <Edit sx={{ color: "#5bc0de" }} />
                  </IconButton>
                </Tooltip>
              }
            />
      
          </Grid> */}
          <Grid item xs={12} md={8}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography variant="h6" color="white">
                    {order.order_number}

                  </MDTypography>
                  <MDBox>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {recordActions}
                    </Box>

                    {/* If you have any modals (Edit, Clone, etc.), you can add them here following 
      the same styling pattern as before */}
                  </MDBox>

                </MDBox>
                {renderStepper()}
              </MDBox>

              <MDBox>
                {recordDetailsBody}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            {recordSideColumn}
          </Grid>
        </Grid>
      </MDBox>
      )}
      
      <EditOrderModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        order={order}
        orderItems={order.order_items}
        onSave={handleSaveOrderChanges}
        suggestions={suggestions}
        productList={productList}
        transportDetails={{
          transid: order.transid,
          transname: order.transname,
          transmode: order.transmode,
          vehno: order.vehno,
          vehtype: order.vehtype,
          transdocno: order.transdocno,
          // transdocdate: order.transdocdate || null,
        }}
      />
      <ActivityModal
        isOpen={isActivityModalOpen}
        onClose={handleCloseActivityModal}
        parentId={id}
        parent="orders"
        axiosInstance={axiosInstance}
        onActivitySaved={handleActivitySaved}
        currentUser={user}
      />
      <CloneOrderModal
        open={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        order={order}
        orderItems={order.order_items}
        onSave={handleCloneOrder} // Pass the clone function here
        suggestions={suggestions}
        productList={productList}
        user={user}
        transportDetails={{
          transid: order.transid,
          transname: order.transname,
          transmode: order.transmode,
          vehno: order.vehno,
          vehtype: order.vehtype,
          transdocno: order.transdocno,
          // transdocdate: order.transdocdate || null,
        }}
      />
      {/* Existing Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* <Dialog
        open={openInvoiceDialog}
        onClose={() => setOpenInvoiceDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <div dangerouslySetInnerHTML={{ __html: currentInvoiceHtml }} />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenInvoiceDialog(false)} color="primary">
            Close
          </MDButton>
        </DialogActions>
      </Dialog> */}

      <Dialog open={isDownloading}>
        <DialogContent className="sm:max-w-[425px]">
          <div className="flex flex-col items-center justify-center p-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <h2 className="text-lg font-semibold mb-2">Generating Invoice</h2>
            <p className="text-center text-sm text-muted-foreground">
              Please wait while we prepare your invoice for download. This may take a few moments.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openEditInvoiceModal}
        onClose={() => setOpenEditInvoiceModal(false)}
        sx={{ '& .MuiDialog-paper': { width: '1000px', maxWidth: '90%' } }}
      >
        <DialogContent>
          <MDTypography variant="h6">Edit Invoice</MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => option?.label || ''}
                value={accounts.find(acc => acc.value === editedInvoice?.account_id) || null}
                onChange={(e, newValue) => {
                  handleInvoiceFieldChange('account_id', newValue?.value);
                  handleInvoiceFieldChange('account_name', newValue?.label);
                  handleInvoiceFieldChange('account_currency', newValue?.currency);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Account"
                    fullWidth
                    margin="normal"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "45px"
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Invoice Date"
                type="date"
                value={editedInvoice?.invoice_date || ''}
                onChange={(e) => handleInvoiceFieldChange('invoice_date', e.target.value)}
                fullWidth
                margin="normal"
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Due Date"
                type="date"
                value={editedInvoice?.due_date || ''}
                onChange={(e) => handleInvoiceFieldChange('due_date', e.target.value)}
                fullWidth
                margin="normal"
                disabled={!isEditable}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={['Draft', 'Sent', 'Completed', 'Cancelled']}
                value={editedInvoice?.status || ''}
                onChange={(e, newValue) => handleInvoiceFieldChange('status', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Invoice Status"
                    fullWidth
                    margin="normal"
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                    disabled={!isEditable}
                  />
                )}
              />
            </Grid>
            <Grid item md={12} xs={6} >
              <TextField
                label="Notes"
                value={editedInvoice?.notes || ''}
                onChange={(e) => handleInvoiceFieldChange('notes', e.target.value)}
                fullWidth
                multiline
                rows={4}
                margin="normal"
                disabled={!isEditable}
              />
            </Grid>

            {/* Transport Details Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <MDTypography variant="subtitle2" fontWeight="medium" mb={2}>
                Transport Details
              </MDTypography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Transporter ID"
                value={editedInvoice?.transid || ''}
                onChange={(e) => handleInvoiceFieldChange('transid', e.target.value)}
                fullWidth
                margin="normal"
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Transporter Name"
                value={editedInvoice?.transname || ''}
                onChange={(e) => handleInvoiceFieldChange('transname', e.target.value)}
                fullWidth
                margin="normal"
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={[
                  { label: 'Maritime', value: '1' },
                  { label: 'Rail', value: '2' },
                  { label: 'Road Transport', value: '3' },
                  { label: 'Air', value: '4' }
                ]}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    const mapping = {
                      '1': 'Maritime',
                      '2': 'Rail',
                      '3': 'Road Transport',
                      '4': 'Air'
                    };
                    return mapping[option] || option;
                  }
                  return option.label;
                }}
                value={
                  editedInvoice?.transmode
                    ? (typeof editedInvoice.transmode === 'string' && ['1', '2', '3', '4'].includes(editedInvoice.transmode))
                      ? {
                        label: {
                          '1': 'Maritime',
                          '2': 'Rail',
                          '3': 'Road Transport',
                          '4': 'Air'
                        }[editedInvoice.transmode],
                        value: editedInvoice.transmode
                      }
                      : editedInvoice.transmode
                    : null
                }
                onChange={(e, newValue) => {
                  handleInvoiceFieldChange('transmode', newValue ? newValue.value : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Transport Mode"
                    fullWidth
                    margin="normal"
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                    disabled={!isEditable}
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Vehicle Number"
                value={editedInvoice?.vehno || ''}
                onChange={(e) => handleInvoiceFieldChange('vehno', e.target.value)}
                fullWidth
                margin="normal"
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={[
                  { label: 'Regular', value: 'R' },
                  { label: 'Container', value: 'C' },
                  { label: 'Over Dimensional Cargo', value: 'O' },
                ]}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    const mapping = {
                      'R': 'Regular',
                      'O': 'Over Dimensional Cargo',
                      'C': 'Container'
                    };
                    return mapping[option] || option;
                  }
                  return option.label;
                }}
                value={
                  editedInvoice?.vehtype
                    ? (typeof editedInvoice.vehtype === 'string' && ['R', 'O', 'C'].includes(editedInvoice.vehtype))
                      ? {
                        label: {
                          'R': 'Regular',
                          'O': 'Over Dimensional Cargo',
                          'C': 'Container'
                        }[editedInvoice.vehtype],
                        value: editedInvoice.vehtype
                      }
                      : editedInvoice.vehtype
                    : null
                }
                onChange={(e, newValue) => {
                  handleInvoiceFieldChange('vehtype', newValue ? newValue.value : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehicle Type"
                    fullWidth
                    margin="normal"
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      },
                    }}
                    disabled={!isEditable}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* <DialogContent> */}
          <MDBox
            sx={{
              mt: 3,
              mb: 3,
              border: "1px solid #dee2e6",
              borderRadius: "0.75rem",
              overflow: "auto",
              maxHeight: "400px"
            }}
          >
            {/* Header */}
            <MDBox
              sx={{
                display: "flex",
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #dee2e6",
                position: "sticky",
                top: 0,
                zIndex: 1
              }}
            >
              <MDBox width="25%" p={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Product Name
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Unit Price
                </MDTypography>
              </MDBox>
              <MDBox width="8%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Tax %
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Discount %
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Discounted Price
                </MDTypography>
              </MDBox>
              <MDBox width="10%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Quantity
                </MDTypography>
              </MDBox>
              <MDBox width="12%" p={2} textAlign="right">
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Total Price
                </MDTypography>
              </MDBox>
              <MDBox width="9%" p={2}>
                <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase">
                  Actions
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Body */}
            <MDBox>
              {editedInvoiceItems
                .filter(item => !item.markedForDeletion)
                .map((item, index) => {
                  const discountedPrice = (item.unit_price || 0) - ((item.unit_price || 0) * (item.discount_percentage || 0) / 100);
                  const totalNetPrice = discountedPrice * (item.quantity || 0);
                  return (
                    <MDBox
                      key={item.tempId || index}
                      display="flex"
                      sx={{
                        borderBottom: "1px solid #dee2e6",
                        '&:hover': { backgroundColor: '#f8f9fa' }
                      }}
                    >
                      <MDBox width="25%" p={2}>
                        <Autocomplete
                          options={productList}
                          getOptionLabel={(option) => option.name}
                          value={productList.find((product) => product.name === item.product_name) || null}
                          onChange={(e, newValue) =>
                            handleInvoiceItemChange(item.tempId, 'product_name', newValue ? newValue.name : '')
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': { borderColor: '#dee2e6' },
                                  '&:hover fieldset': { borderColor: '#7b809a' }
                                }
                              }}
                            />
                          )}
                          disabled={!isEditable}
                        />
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {formatCurrencyByAccount(item.unit_price || 0, getOrderDisplayCurrency())}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="8%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {item.tax || 0}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <TextField
                          value={item.discount_percentage || 0}
                          onChange={(e) =>
                            handleInvoiceItemChange(
                              item.tempId,
                              'discount_percentage',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          type="number"
                          disabled={!isEditable}
                          size="small"
                          sx={{
                            width: '70px',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#dee2e6' },
                              '&:hover fieldset': { borderColor: '#7b809a' }
                            }
                          }}
                        />
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {formatCurrencyByAccount(discountedPrice || 0, getOrderDisplayCurrency())}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="10%" p={2} textAlign="right">
                        <TextField
                          value={item.quantity || 0}
                          onChange={(e) =>
                            handleInvoiceItemChange(
                              item.tempId,
                              'quantity',
                              parseInt(e.target.value, 10) || 0
                            )
                          }
                          type="number"
                          size="small"
                          disabled={!isEditable}
                          sx={{
                            width: '70px',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#dee2e6' },
                              '&:hover fieldset': { borderColor: '#7b809a' }
                            }
                          }}
                        />
                      </MDBox>
                      <MDBox width="12%" p={2} textAlign="right">
                        <MDTypography variant="button" fontWeight="regular">
                          {formatCurrencyByAccount(totalNetPrice || 0, getOrderDisplayCurrency())}
                        </MDTypography>
                      </MDBox>
                      <MDBox width="9%" p={2} display="flex" alignItems="center">
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveInvoiceItem(item.tempId)}
                          disabled={!isEditable || editedInvoiceItems.filter(i => !i.markedForDeletion).length <= 1}
                          size="small"
                        >
                          <RemoveIcon />
                        </IconButton>
                        <IconButton
                          color="info"
                          onClick={handleAddInvoiceItem}
                          disabled={!isEditable}
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </MDBox>
                    </MDBox>
                  );
                })}
            </MDBox>
          </MDBox>

          {/* Totals Section */}
          <MDBox
            sx={{
              mt: 3,
              p: 2,
              borderTop: '2px solid #dee2e6',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <MDBox sx={{ minWidth: '300px' }}>
              {(() => {
                const { subtotal, grandTotal, taxSummary } = calculateTotalAmount();
                const currency = getOrderDisplayCurrency();

                return (
                  <MDBox>
                    <MDBox display="flex" justifyContent="space-between" mb={1}>
                      <MDTypography variant="button" color="text" fontWeight="regular">
                        Subtotal:
                      </MDTypography>
                      <MDTypography variant="button" color="text" fontWeight="medium">
                        {formatCurrencyByAccount(subtotal, currency)}
                      </MDTypography>
                    </MDBox>

                    {Object.entries(taxSummary).map(([taxRate, taxAmount]) => (
                      <MDBox key={taxRate} display="flex" justifyContent="space-between" mb={1}>
                        <MDTypography variant="button" color="text" fontWeight="regular">
                          Tax ({taxRate}%):
                        </MDTypography>
                        <MDTypography variant="button" color="text" fontWeight="medium">
                          {formatCurrencyByAccount(taxAmount, currency)}
                        </MDTypography>
                      </MDBox>
                    ))}

                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      mt={2}
                      pt={1.5}
                      sx={{ borderTop: '1px solid #dee2e6' }}
                    >
                      <MDTypography variant="button" color="dark" fontWeight="medium">
                        Total Amount:
                      </MDTypography>
                      <MDTypography variant="button" color="info" fontWeight="bold">
                        {formatCurrencyByAccount(grandTotal, currency)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })()}
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <MDButton
            onClick={() => setOpenEditInvoiceModal(false)}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSaveInvoice}
            variant="gradient"
            color="info"
          >
            Save
          </MDButton>
        </DialogActions>
        {/* </DialogContent> */}
      </Dialog>
      <EmailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        handleSend={handleSendEmail}
        isGenerating={isGeneratingEmail}
        isSending={isEmailSending}
        errorMessage={emailError}
        emailContext={emailContext}
        setEmailContext={setEmailContext}
        handleGenerateEmail={handleGenerateEmail}
        showCc={showCc}
        showBcc={showBcc}
        toggleCc={toggleCc}
        toggleBcc={toggleBcc}
        handleQuillChange={handleQuillChange}
        modules={quillModules}
        formats={quillFormats}
        attachments={emailAttachments}
        setEmailAttachments={setEmailAttachments}
      />
      <InvoiceViewer
        open={openViewInvoiceModal}
        onClose={() => setOpenViewInvoiceModal(false)}
        invoice={viewInvoice}
        invoiceItems={viewInvoiceItems}
        onSendEmail={handleToggleEmailModal}
        isEmailLoading={isEmailLoading}
        formatCurrencyByAccount={formatCurrencyByAccount}
        colors={colors}
      />
      {/* <MDButton
        variant="gradient"
        color="info"
        onClick={handleOrderPdfDownload}
      >
        Download Order PDF
      </MDButton> */}

      <Dialog
        open={isInvoiceDialogOpen}
        onClose={handleCloseInvoiceDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { width: '600px', maxWidth: '90vw' }
        }}
      >
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>

          <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Split Invoice By</FormLabel>
          <RadioGroup
            row
            value={splitMode}
            onChange={e => setSplitMode(e.target.value)}
          >
            <FormControlLabel value="percentage" control={<Radio />} label="Percentage" />
            <FormControlLabel value="amount" control={<Radio />} label="Amount" />
          </RadioGroup>
          {splitMode === 'percentage' ? (
            <TextField
              label="Invoice Percentage (%)"
              type="number"
              value={invoicePercent}
              onChange={e => setInvoicePercent(Number(e.target.value))}
              error={!!invoicePercentError}
              helperText={
                invoicePercentError ||
                `Max: ${getInvoiceSplitTotals().remainingPercent.toFixed(2)}% left to invoice`
              }
              fullWidth
              margin="normal"
              inputProps={{ min: 0.01, max: getInvoiceSplitTotals().remainingPercent || 100, step: '0.01' }}
            />
          ) : (
            <TextField
              label={`Invoice Amount (${order.currency || 'INR'})`}
              type="number"
              value={invoiceAmount}
              onChange={e => setInvoiceAmount(e.target.value)}
              error={!!invoicePercentError}
              helperText={
                invoicePercentError ||
                `Max: ${formatCurrencyByAccount(getInvoiceSplitTotals().displayRemaining, getOrderDisplayCurrency())} left to invoice`
              }
              fullWidth
              margin="normal"
              inputProps={{ min: 0.01, max: getInvoiceSplitTotals().displayRemaining, step: '0.01' }}
            />
          )}

          {/* Invoice Status Summary */}
          <Box sx={{
            mb: 3,
            mt: 2,
            p: 2,
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Typography variant="subtitle2" sx={{
              fontWeight: 600,
              color: 'text.info',
              mb: 1.5,
              textAlign: 'center'
            }}>
              Invoice Summary
            </Typography>

            {(() => {
              const { displayInvoiced, displayRemaining, invoicedPercent, remainingPercent } = getInvoiceSplitTotals();
              const summaryCurrency = getOrderDisplayCurrency();
              return (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                      Invoiced:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrencyByAccount(displayInvoiced, summaryCurrency)}
                      <span style={{ color: '#666', fontWeight: 400 }}>
                        ({invoicedPercent.toFixed(2)}%)
                      </span>
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'warning.main' }}>
                      Remaining:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrencyByAccount(displayRemaining, summaryCurrency)}
                      <span style={{ color: '#666', fontWeight: 400 }}>
                        ({remainingPercent.toFixed(2)}%)
                      </span>
                    </Typography>
                  </Box>
                </>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvoiceDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmInvoiceDialog}
            variant="contained"
            color="primary"
            disabled={isInvoiceButtonLoading}
            sx={{ color: 'white !important' }}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <StatusStepperConfirmDialog
        open={orderStatusConfirm.open}
        title="Change order status"
        message={
          orderStatusConfirm.index != null
            ? orderStages[orderStatusConfirm.index] === 'Cancelled'
              ? 'Are you sure you want to mark this order as Cancelled? Inventory will be restored and this status is hard to reverse.'
              : `Are you sure you want to mark this order as ${orderStages[orderStatusConfirm.index]}?`
            : ''
        }
        confirmColor={
          orderStatusConfirm.index != null && orderStages[orderStatusConfirm.index] === 'Cancelled'
            ? 'error'
            : 'primary'
        }
        loading={isDetailsLoading}
        onCancel={handleOrderStatusConfirmCancel}
        onConfirm={handleOrderStatusConfirm}
      />

      {/* Feed Item Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="feed-delete-modal-title"
        aria-describedby="feed-delete-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <MDTypography
              id="feed-delete-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}
            >
              Delete Message
            </MDTypography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            <MDTypography
              id="feed-delete-modal-description"
              variant="body1"
              mb={3}
              sx={{ color: '#34495e' }}
            >
              Are you sure you want to delete this message? This action cannot be undone.
            </MDTypography>

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                onClick={handleDeleteCancel}
                variant="outlined"
                color="secondary"
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleDeleteConfirmOk}
                variant="gradient"
                color="error"
              >
                Delete
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>
    </DashboardLayout>
  );
}

export default OrderDetail